import React, { Component } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { FB_URL } from '../store/actions/types';
import NotificationStatus from '../components/NotificationStatus';
import firebase from '../store/firebase'

import MathRenderer   from './MathRenderer';
import MathKeyboard   from './MathKeyboard';
import AgentFocusModal from './AgentFocusModal';

import AudioBubble from './AudioBubble';

import welcomeAudio from './DennisImg/welcome_message.mp4'; // or .m4a, see note 2

const WELCOME_BY_AGENT = {
  dennis: {
    text:
      "Hi, I'm Dennis 👋\n\n" +
      "I'm glad you're here. I've left you a short voice note below explaining how we can work together. " +
      "Have a listen, then tell me a bit about yourself: what you're working on, or what you'd like to get better at.",
    audio: welcomeAudio,
  },
};

// ─── TypingIndicator — animated dots with random "thinking" pauses ──────────
const TypingIndicator = () => {
  const [isPaused, setIsPaused] = React.useState(false);

  React.useEffect(() => {
    // Randomly pause the animation to feel like the agent is thinking
    let timeoutId;
    const cycle = () => {
      // Animate for 2–4 seconds
      const animateDuration = 2000 + Math.random() * 2000;
      setIsPaused(false);
      timeoutId = setTimeout(() => {
        // Pause for 0.6–1.8 seconds
        const pauseDuration = 600 + Math.random() * 1200;
        setIsPaused(true);
        timeoutId = setTimeout(cycle, pauseDuration);
      }, animateDuration);
    };
    cycle();
    return () => clearTimeout(timeoutId);
  }, []);

  const dotBase = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#7a9bb5',
    display: 'inline-block',
    margin: '0 3px',
    animation: isPaused
      ? 'none'
      : 'typingBob 1.2s infinite ease-in-out',
  };

  return (
    <>
      {/* Keyframes injected once — safe to include multiple times */}
      <style>{`
        @keyframes typingBob {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-5px);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{
        alignSelf: 'flex-start',
        padding: '12px 16px',
        borderRadius: '12px 12px 12px 2px',
        backgroundColor: '#ffffff',
        border: '1px solid rgba(26,111,186,0.15)',
        display: 'inline-flex',
        alignItems: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
      }}>
        <span style={{ ...dotBase, animationDelay: '0s' }} />
        <span style={{ ...dotBase, animationDelay: '0.2s' }} />
        <span style={{ ...dotBase, animationDelay: '0.4s' }} />
      </div>
    </>
  );
};

// ─── Notification pop sound ───────────────────────────────────────
// Generates a short "pop" via Web Audio API — no asset needed.
// Called when a new agent message arrives so the user notices even
// if they're glancing elsewhere.
const playPopSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    // Two-note "pop" — quick rise + fade
    osc.frequency.setValueAtTime(660, now);         // starting pitch
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);  // rises

    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02); // quick attack
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15); // fade

    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.2);
    osc.onended = () => ctx.close();
  } catch (e) {
    // Silent fail — audio unavailable, no big deal
    console.warn('playPopSound error:', e.message);
  }
};

// ─── Agent catalogue ─────────────────────────────────────────────────────────
const ALL_AGENTS = [
  // ── Universal ──
  { id: 'wisdom',           label: 'Wisdom Agent',             icon: '🌿', category: 'Universal',  description: 'Your daily Human Powers development partner. The foundation of everything.' },
  { id: 'wisdomCompanion',  label: 'Wisdom Companion',         icon: '✨', category: 'Universal',  description: 'Enhanced companion with Lc rewards, certificates, and article tests.' },
  { id: 'livewell',         label: 'Live Well Agent',          icon: '💚', category: 'Universal',  description: 'Daily health and wellness. Body, mind, sleep, energy, nutrition.' },
  { id: 'sales',            label: 'Sales Agent',              icon: '🎯', category: 'Universal',  description: 'For everyone who sells — professionals, network marketers, small business owners.' },
  { id: 'ldsdiscipleship',  label: 'LDS Discipleship Partner', icon: '📖', category: 'Universal',  description: 'Daily companion on the covenant path for Latter-day Saint members.' },
  { id: 'smallbusiness',    label: 'Small Business Agent',     icon: '💼', category: 'Universal',  description: 'Your daily CFO, sales director, marketing manager, and thinking partner.' },
  { id: 'parent',           label: 'Parent Agent',             icon: '👨‍👩‍👧', category: 'Universal',  description: 'Daily development for parents — growing in the Human Powers so your children watch you become the person they need to learn from.' },
  { id: 'athlete',          label: 'Athlete Development Agent',icon: '🏅', category: 'Universal',  description: 'Daily mental and character development for serious athletes at every level and in every sport — closing the gap between training performance and competition performance.' },
  { id: 'relationship',     label: 'Relationship Partner',     icon: '💑', category: 'Universal',  description: 'Daily development for the human being in their most important relationship — developing Agency, Accountability, Commitment, and Heart so the relationship improves because the person does.' },
  { id: 'lpower',           label: 'LPOWER Life Path',         icon: '🧭', category: 'Universal',  description: 'Discover your gifts, your deepest drivers, and what you are truly for — and the path where that meets the world that is coming. Socratic. For any age.' },
  { id: 'communication',    label: 'Communication Agent',      icon: '🎙️', category: 'Universal',  description: 'Find your genuine voice — confidence, authenticity, storytelling, and the courage to say the true thing. For young people and leaders alike, by voice and text.' },
  { id: 'golf',             label: 'Golf Development Partner', icon: '⛳', category: 'Universal',  description: 'A specialised Athlete Development Partner for the mental, strategic and character game of golf — never the swing.' },
  { id: 'dennis',           label: 'Dennis — Your Super Development Partner', icon: '🌀', avatar: `${process.env.PUBLIC_URL}/agents/dennis-avatar-256.png`, category: 'Universal', description: '...' },  { id: 'hrleadership',     label: 'HR Leadership Agent',      icon: '🤝', category: 'Leadership', description: 'For HR professionals who carry responsibility for every person in the organisation.' },
  { id: 'schoolleadership', label: 'School Leadership Agent',  icon: '🏫', category: 'Leadership', description: 'For principals, deputy principals, department heads, and teachers.' },
  { id: 'publicservant',    label: 'Public Servant Agent',     icon: '🏛️', category: 'Leadership', description: 'For leaders in government — local, provincial, national, and elected.' },
  { id: 'projectmanagement',label: 'Project Management Agent', icon: '🏗️', category: 'Leadership', description: 'Daily development for project managers, programme managers, and project executives — the courage, accountability, and honest relationships that large projects actually require.' },
  { id: 'highereducation',  label: 'Higher Education Partner', icon: '🎓', category: 'Leadership', description: 'A private thinking partner for presidents, chancellors, deans and cabinets — the one place to think out loud without political cost, available at any hour.' },

  // ── Industry ──
  { id: 'banking',          label: 'Banking Leadership Agent', icon: '🏦', category: 'Industry',   description: 'For banking leaders navigating disruption, transformation, and customer trust.' },
  { id: 'powerutility',     label: 'Power Utility Agent',      icon: '⚡',  category: 'Industry',   description: 'For leaders in power utilities — Eskom, ZESCO, Kenya Power, and beyond.' },
  { id: 'mining',           label: 'Mining Leadership Agent',  icon: '⛏️', category: 'Industry',   description: 'For leaders in the mining industry across all roles and operations.' },
  { id: 'manufacturing',    label: 'Manufacturing Agent',      icon: '🏭', category: 'Industry',   description: 'For leaders in industrial operations — safety, accountability, people.' },
  { id: 'callcentre',       label: 'Call Centre Partner',      icon: '🎧', category: 'Industry',   description: 'Daily development for contact centre agents and leaders — resilience, sales mastery, leadership, and reading the motive behind every call.' },

  // ── Partners ──
  { id: 'harmonyleadership',label: 'Harmony Gold Partner',     icon: '🌟', category: 'Partners',   description: 'Built exclusively for Harmony Gold leaders from Shift Boss to Group Executive.' },
  { id: 'mogalakwena',      label: 'Mogalakwena Partner',      icon: '💎', category: 'Partners',   description: "For leaders of the world's largest open-pit PGM mine — Valterra Platinum." },
  { id: 'implats',          label: 'Implats Partner',          icon: '🔷', category: 'Partners',   description: 'For Impala Platinum leaders across six operations in three countries.' },
  { id: 'eskomguardian',    label: 'Eskom Guardian Partner',   icon: '🔆', category: 'Partners',   description: "For Eskom Guardians — the people who kept South Africa's lights on." },
  { id: 'profmed',          label: 'Profmed Partner',          icon: '🩺', category: 'Partners',   description: 'For Profmed members — developing the whole professional, not just the career.' },
  { id: 'elm',              label: 'Elm Leadership Partner',   icon: '🇸🇦', category: 'Partners',   description: 'Daily leadership development for Elm Company leaders at the intersection of Vision 2030 and digital transformation.',  },
  { id: 'ethics',           label: 'TEI Ethics Partner',       icon: '⚖️', category: 'Partners',   description: 'Daily personal ethics development built with The Ethics Institute — moral courage, ethical leadership, and AI ethics, one honest conversation at a time.' },
  { id: 'alexforbes',       label: 'Alexforbes Partner',       icon: '📊', category: 'Partners',   description: 'Daily development for every Alexforbes adviser, consultant and leader — consistency, follow-through, best advice, and the Human Powers that make the strategy real.' },

  // ── Books ──
  { id: 'peoplecircle',     label: 'People Circle Partner',    icon: '🌍', category: 'Books',      description: "Formation companion grounded in Nqobile Pamela Xaba's People Circle." },
  { id: 'culturethatholds', label: 'Culture That Holds',       icon: '🛡️', category: 'Books',      description: "A practical companion to Eddie Botes' book — diagnose the cultural pattern beneath your team's problem, then move to one behaviour worth testing." },
  { id: 'ceohandbook',      label: 'CEO Handbook Partner',     icon: '🧭', category: 'Books',      description: 'A daily companion built on the CEO Leadership Handbook — the Destiny Chain, the attitudinal modes, and moving people and situations forward profitably.' },

  // ── Education ──
  { id: 'mpower',           label: 'MPOWER Maths Partner',     icon: '📐', category: 'Education',  description: 'Daily CAPS mathematics development for Grade 10, 11, and 12 learners — capability and Human Powers simultaneously.' },
  { id: 'spower',           label: 'SPOWER Sciences Partner',  icon: '🧪', category: 'Education',  description: 'Daily CAPS Physical and Life Sciences development for Grade 10, 11, and 12 learners — scientific capability and the 8 Human Powers, together.' },
  { id: 'aipower',          label: 'AIPOWER Partner',          icon: '🤖', category: 'Education',  description: 'Lead AI instead of being led by it — technical understanding, practical mastery, ethics, and the confidence to stay fully human in an AI world.' },
  { id: 'rpower',           label: 'RPOWER Reading Partner',   icon: '📚', category: 'Education',  description: 'Private, patient, shame-free reading development for older learners — rebuilding confidence and human powers alongside the skill, one session at a time.' },
  { id: 'mpower89',         label: 'MPOWER Grade 8 & 9',       icon: '🔢', category: 'Education',  description: 'Daily maths development for Grade 8 and 9 learners — catching the gaps early, keeping every door open, and building a maths identity before it hardens.' },
  { id: 'spower89',         label: 'SPOWER Grade 8 & 9',       icon: '🔬', category: 'Education',  description: 'Daily science development for Grade 8 and 9 learners — keeping curiosity alive across all four strands, and keeping every science door open before the subject-choice moment.' },
  { id: 'taalpower',        label: 'TaalPower Afrikaans',      icon: '🇿🇦', category: 'Education',  description: 'Jou Afrikaans-vennoot vir Graad 10–12 — Huistaal en EAT. Patient, personal, unafraid practice in the language that can make or break the year.' },
  { id: 'engpower',         label: 'EngPower English',         icon: '📝', category: 'Education',  description: 'Your English partner for Grade 10–12 — HL, FAL and SAL. Master the language you are taught and examined in across every subject, and you lift everything.' },
  { id: 'zulupower',        label: 'ZuluPower isiZulu',        icon: '🗣️', category: 'Universal',  description: 'Learn to speak isiZulu from your very first word — voice-first, culture-first, for adults and learners. Sawubona means I see you. Ubuntu, one spoken word at a time.' },
  { id: 'phosachair',       label: 'Phosa Chair Partner',      icon: '🕊️', category: 'Partners',   description: 'Leadership development partner of the Dr Mathews Phosa Chair at Nelson Mandela University — diplomacy, governance, African futures, and the standard that power is meaningless without service.' },
];

const CATEGORIES = ['All', 'Universal', 'Leadership', 'Industry', 'Partners', 'Books', 'Education'];

const VOICE_OPTIONS = [
  { id: 'alloy',   label: 'Alloy',   description: 'Neutral · warm' },
  { id: 'echo',    label: 'Echo',    description: 'Male · calm' },
  { id: 'fable',   label: 'Fable',   description: 'Male · British storyteller' },
  { id: 'onyx',    label: 'Onyx',    description: 'Male · deeper, authoritative' },
  { id: 'nova',    label: 'Nova',    description: 'Female · bright' },
  { id: 'shimmer', label: 'Shimmer', description: 'Female · soft' },
];

const VOICE_PREVIEW_URL = (voice) =>
  `https://storage.googleapis.com/leadershipplatform-158316.appspot.com/voicePreviews/${voice}.mp3`;
// ─── Responsive helper — 900px catches most tablets and phones ───────────────
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 900;

// ─── Colour palette — light blue theme ───────────────────────────────────────
const FONT          = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const FONT_AI       = "'Space Grotesk', 'Segoe UI', sans-serif";
const BG            = '#eef4fb';
const BG_CARD       = '#ffffff';
const BG_CARD_A     = '#ddeeff';
const BG_HEADER     = '#dce8f5';
const BG_CHAT       = '#f4f8fd';
const BG_INPUT      = '#ffffff';
const BG_BUBBLE_USR = '#1a6fba';
const BG_BUBBLE_AGT = '#ffffff';
const BORDER        = 'rgba(26,111,186,0.15)';
const BORDER_A      = 'rgba(26,111,186,0.55)';
const TEXT_H        = '#0d2b4a';
const TEXT_BODY     = '#1a3a5c';
const TEXT_MID      = '#3a6080';
const TEXT_MUTE     = '#7a9bb5';
const ACCENT        = '#1a6fba';
const ACCENT_F      = 'rgba(26,111,186,0.10)';
const ACCENT_B      = 'rgba(26,111,186,0.45)';

const S = {
  // page: {
  //   minHeight: '100vh', backgroundColor: BG, color: TEXT_BODY,
  //   fontFamily: FONT, display: 'flex', flexDirection: 'column',
  // },
  page: {
    height: '100vh', overflow: 'hidden', backgroundColor: BG, color: TEXT_BODY,
    fontFamily: FONT, display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '20px 16px 16px', borderBottom: `1px solid ${BORDER}`,
    backgroundColor: BG_HEADER,
  },
  headerTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '16px', flexWrap: 'wrap', gap: '8px',
  },
  logoMark: {
    fontSize: '10px', letterSpacing: '3px', color: ACCENT,
    textTransform: 'uppercase', marginBottom: '3px', fontWeight: '600',
  },
    headerTopRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: '4px 16px', marginBottom: '8px',
  },
  statsInline: {
    display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
    fontSize: '11px', color: TEXT_MID, position: 'relative',
  },
  statsValue: { color: ACCENT, fontWeight: '700' },
  statsDot: { color: TEXT_MUTE },
  connectedAgentsRowInline: {
    fontSize: '11px', color: TEXT_MID,
    cursor: 'pointer', textDecoration: 'underline',
  },
  headerTitle: (secondary) => ({
    fontSize: secondary ? '14px' : '22px',
    fontWeight: secondary ? '600' : '700',
    color: secondary ? TEXT_MUTE : TEXT_H,
    margin: 0, letterSpacing: '-0.3px', fontFamily: FONT,
    transition: 'all 0.2s',
  }),
  headerSubtitle: { fontSize: '12px', color: TEXT_MID, marginTop: '3px' },
  pledgeBadge:    { textAlign: 'right' },
  pledgeLabel: {
    fontSize: '10px', color: TEXT_MUTE, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '2px',
  },
  pledgeValue: { fontSize: '17px', color: ACCENT, fontWeight: '700', fontFamily: FONT },
  toolbar: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  tabs: { display: 'flex', gap: '3px', flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '6px 14px', fontSize: '11px', letterSpacing: '1px',
    textTransform: 'uppercase',
    border: `1px solid ${active ? ACCENT : BORDER}`,
    borderRadius: '3px', cursor: 'pointer', transition: 'all 0.2s',
    backgroundColor: active ? ACCENT : 'transparent',
    color: active ? '#ffffff' : TEXT_MID,
    fontWeight: active ? '600' : '400', fontFamily: FONT,
  }),
  viewToggle: {
    display: 'flex', border: `1px solid ${BORDER}`,
    borderRadius: '3px', overflow: 'hidden', flexShrink: 0,
  },
  viewBtn: (active) => ({
    padding: '5px 12px', fontSize: '14px', border: 'none', cursor: 'pointer',
    backgroundColor: active ? ACCENT_F : 'transparent',
    color: active ? ACCENT : TEXT_MUTE,
    transition: 'all 0.15s', lineHeight: 1,
  }),
  main: { flex: 1, display: 'flex', gap: '0', position: 'relative', minHeight: 0, overflow: 'hidden' },
  agentPanel: (chatOpen, mobile) => ({
    flex: chatOpen && !mobile ? '0 0 auto' : 1,
    width: chatOpen && !mobile ? 'auto' : '100%',
    display: chatOpen && mobile ? 'none' : 'flex',
    flexDirection: 'column',
    overflowY: 'auto', padding: '16px', minHeight: 0,
  }),
  // ── Expanded cards ──
  gridExpanded: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '10px', alignContent: 'start',
  },
  cardExpanded: (active) => ({
    border: `1px solid ${active ? BORDER_A : BORDER}`,
    borderRadius: '6px', padding: '14px', cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: active ? BG_CARD_A : BG_CARD,
    position: 'relative', overflow: 'hidden',
    boxShadow: active ? '0 2px 8px rgba(26,111,186,0.12)' : '0 1px 3px rgba(0,0,0,0.05)',
  }),
  cardGlow: {
    position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
    background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
  },
  cardIconLg:   { fontSize: '20px', marginBottom: '8px', display: 'block' },
  cardCategory: { fontSize: '9px', letterSpacing: '2px', color: TEXT_MUTE, textTransform: 'uppercase', marginBottom: '3px' },
  cardLabel:    { fontSize: '13px', color: TEXT_H, fontWeight: '600', marginBottom: '6px', lineHeight: '1.3', fontFamily: FONT },
  cardDesc:     { fontSize: '11px', color: TEXT_MID, lineHeight: '1.5' },
  cardOpenBtn:  {
    marginTop: '10px', fontSize: '10px', letterSpacing: '1px',
    textTransform: 'uppercase', color: ACCENT, background: 'none',
    border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT, fontWeight: '600',
  },
  // ── Compact cards ──
  gridCompact: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
    gap: '8px', alignContent: 'start',
  },
  cardCompact: (active) => ({
    border: `1px solid ${active ? BORDER_A : BORDER}`,
    borderRadius: '6px', padding: '10px 6px', cursor: 'pointer',
    transition: 'all 0.15s',
    backgroundColor: active ? BG_CARD_A : BG_CARD,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    position: 'relative',
    boxShadow: active ? '0 2px 6px rgba(26,111,186,0.15)' : '0 1px 2px rgba(0,0,0,0.04)',
  }),
  cardIconSm:  { fontSize: '22px', lineHeight: 1 },
  cardLabelSm: {
    fontSize: '8px', color: TEXT_MID, textAlign: 'center',
    lineHeight: '1.2', fontFamily: FONT,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%',
  },
  activeDot: {
    position: 'absolute', top: '4px', right: '4px',
    width: '6px', height: '6px', borderRadius: '50%', backgroundColor: ACCENT,
  },
  // ── Chat panel ──
  chatPanel: (mobile, full) => ({
    // On mobile: flex:1 fills the main container naturally — no fixed/absolute,
    // no z-index, no 100vh. Works inside any app shell on any deployment.
    // On desktop: sticky 400px sidebar UNLESS `full` — full-width when the
    // agent grid isn't showing alongside it (Dennis-as-default landing view).
    flex: (mobile || full) ? 1 : 'none',
    width: (mobile || full) ? '100%' : '400px',
    minWidth: (mobile || full) ? '0' : '400px',
    maxWidth: (mobile || full) ? '100%' : '400px',
    borderLeft: (mobile || full) ? 'none' : `1px solid ${BORDER}`,
    display: 'flex', flexDirection: 'column',
    height: mobile ? '100%' : '100%',
    position: mobile ? 'relative' : 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: BG_CHAT,
    boxSizing: 'border-box',
    overflow: 'hidden',
  }),
  chatHeader: {
    padding: '10px 16px', borderBottom: `1px solid ${BORDER}`,
    display: 'flex', flexDirection: 'column', gap: '6px',
    backgroundColor: BG_HEADER, flexShrink: 0,
  },
  chatHeaderMainRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
  },
  chatHeaderLinksRow: {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
    paddingLeft: '2px',
  },
  chatHeaderLeft:  { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1, overflow: 'hidden' },
  chatAgentName:   (prominent) => ({
    fontSize: prominent ? '20px' : '13px',
    color: TEXT_H,
    fontWeight: prominent ? '800' : '600',
    fontFamily: FONT_AI, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    letterSpacing: prominent ? '-0.5px' : '-0.1px',
    transition: 'all 0.2s',
    flex: '1 1 auto', minWidth: 0,
  }),
  chatAgentIcon:   (prominent) => ({
    fontSize: prominent ? '32px' : '16px',
    flexShrink: 0, lineHeight: 1,
    transition: 'all 0.2s',
  }),
  // ← Agents button — bordered/prominent by default; quiets to a plain text
  // link in hero mode, since leaving the featured agent is now secondary
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '4px',
    fontSize: '14px', fontWeight: '700', color: ACCENT,
    background: 'none',
    border: `1px solid ${ACCENT}`,
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '5px 10px',
    fontFamily: FONT, flexShrink: 0,
    whiteSpace: 'nowrap',
    marginRight: '8px',
    lineHeight: 1,
  },
  backBtnSubtle: {
    display: 'flex', alignItems: 'center', gap: '4px',
    fontSize: '11px', fontWeight: '500', color: TEXT_MUTE,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 2px',
    fontFamily: FONT, flexShrink: 0,
    whiteSpace: 'nowrap',
    marginRight: '4px',
    lineHeight: 1,
    textDecoration: 'underline',
  },
  closeChatBtn: {
    fontSize: '22px', color: TEXT_MUTE, background: 'none',
    border: 'none', cursor: 'pointer', lineHeight: 1,
    padding: '0 2px', flexShrink: 0,
  },
  messages: {
    flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
bubble: (role) => ({
  maxWidth:        role === 'user' ? '78%' : '88%',
  alignSelf:       role === 'user' ? 'flex-end' : 'flex-start',
  padding:         '10px 14px',
  borderRadius:    role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
  backgroundColor: role === 'user' ? BG_BUBBLE_USR : BG_BUBBLE_AGT,
  border:          `1px solid ${role === 'user' ? 'transparent' : BORDER}`,
  fontSize:        '15px',
  lineHeight:      '1.7',
  color:           role === 'user' ? '#ffffff' : TEXT_BODY,
  fontFamily:      FONT,
  boxShadow:       '0 1px 4px rgba(0,0,0,0.07)',
  // ← Remove overflowX: 'auto' for voice bubbles — it clips the player
  // ← Ensure minimum width for audio player to render fully
  minWidth:        '220px',
  overflowX:       'visible',
}),
  typingIndicator: {
    alignSelf: 'flex-start', padding: '10px 14px',
    borderRadius: '12px 12px 12px 2px',
    backgroundColor: BG_BUBBLE_AGT, border: `1px solid ${BORDER}`,
    color: TEXT_MUTE, fontSize: '18px', letterSpacing: '3px',
  },
  inputRow: {
    padding: '10px 12px', borderTop: `1px solid ${BORDER}`,
    display: 'flex', gap: '6px', alignItems: 'flex-end',
    backgroundColor: BG_HEADER, flexShrink: 0,
    boxSizing: 'border-box', width: '100%',
  },
  textarea: {
    minWidth: 0,
    flex: 1, minWidth: 0, backgroundColor: BG_INPUT,
    border: `1px solid ${BORDER}`, borderRadius: '6px',
    color: TEXT_BODY, fontSize: '15px', lineHeight: '1.6',
    padding: '9px 12px', resize: 'none', outline: 'none',
    fontFamily: FONT, minHeight: '44px', maxHeight: '200px', overflowY: 'auto',
    boxSizing: 'border-box',
  },
  emptyChat: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: TEXT_MUTE, textAlign: 'center', padding: '40px 20px', gap: '10px',
  },
  emptyChatIcon: { fontSize: '32px' },
  emptyChatText: { fontSize: '12px', lineHeight: '1.7', fontStyle: 'italic', fontFamily: FONT, color: TEXT_MID },
  blockedBanner: {
    margin: '14px', padding: '14px',
    border: `1px solid ${ACCENT_B}`, borderRadius: '6px',
    backgroundColor: ACCENT_F,
    fontSize: '12px', color: ACCENT, lineHeight: '1.7', textAlign: 'center', fontFamily: FONT,
  },
  charCount: {
    fontSize: '10px', color: TEXT_MUTE,
    textAlign: 'right', padding: '0 16px 4px', fontFamily: FONT,
  },
// UNSUBSCRIBE STYLE
  unsubscribeLink: {
  fontSize: '11px', color: TEXT_MUTE, background: 'none',
  border: 'none', cursor: 'pointer', textDecoration: 'underline',
  padding: '4px 2px', fontFamily: FONT, flexShrink: 0, whiteSpace: 'nowrap',
},
confirmOverlay: {
  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 5, padding: '20px',
},
confirmBox: {
  backgroundColor: '#ffffff', borderRadius: '10px', padding: '20px',
  maxWidth: '320px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
},
confirmTitle: {
  fontSize: '15px', fontWeight: '700', color: TEXT_H,
  marginBottom: '8px', fontFamily: FONT,
},
confirmBody: {
  fontSize: '13px', color: TEXT_MID, lineHeight: '1.6', marginBottom: '18px',
},
confirmActions: {
  display: 'flex', gap: '8px', justifyContent: 'flex-end',
},
confirmCancelBtn: {
  fontSize: '12px', padding: '8px 14px', borderRadius: '6px',
  border: `1px solid ${BORDER}`, backgroundColor: 'transparent',
  color: TEXT_MID, cursor: 'pointer', fontFamily: FONT,
},
confirmUnsubBtn: {
  fontSize: '12px', padding: '8px 14px', borderRadius: '6px',
  border: 'none', backgroundColor: '#c62828', color: '#fff',
  cursor: 'pointer', fontFamily: FONT, fontWeight: '600',
},
toolBtn: (disabled) => ({
  width: '28px',
  minWidth: '28px',
  maxWidth: '28px',
  height: '28px',
  padding: 0,
  margin: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '4px',
  color: disabled ? TEXT_MUTE : ACCENT,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
}),
focusBtn: {
  background: 'none',
  border: `1px solid ${BORDER}`,
  borderRadius: '4px',
  cursor: 'pointer',
  color: TEXT_MID,
  fontSize: '16px',
  padding: '4px 8px',
  fontFamily: FONT,
  flexShrink: 0,
  lineHeight: 1,
},
mathToggleBtn: (active) => ({
  flexShrink: 0,
  width: '36px',
  height: '36px',
  border: `1px solid ${active ? ACCENT : BORDER}`,
  borderRadius: '6px',
  backgroundColor: active ? ACCENT : 'transparent',
  color: active ? '#fff' : TEXT_MID,
  cursor: 'pointer',
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s',
}),
attachmentChip: {
  display: 'flex', alignItems: 'center', gap: '6px',
  margin: '0 12px 8px', padding: '6px 10px',
  backgroundColor: '#EAF2FB', border: `1px solid ${BORDER}`, borderRadius: '6px',
  fontSize: '13px', color: TEXT_MUTE, maxWidth: 'fit-content',
},
attachmentChipIcon: { fontSize: '14px' },
attachmentChipName: { fontWeight: 500, color: '#1a1a1a', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
attachmentChipRemove: { background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTE, fontSize: '13px', padding: '0 2px' },
pledgeBadge: { textAlign: 'right', position: 'relative' },  // added position: 'relative'
connectedAgentsRow: {
  fontSize: '11px', color: TEXT_MID, marginTop: '4px',
  cursor: 'pointer', textDecoration: 'underline', textAlign: 'right',
},
connectedAgentsList: {
  position: 'absolute', right: 0, marginTop: '4px', zIndex: 10,
  backgroundColor: '#ffffff', border: `1px solid ${BORDER}`, borderRadius: '6px',
  padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  minWidth: '180px', textAlign: 'left',
},
connectedAgentsItem: {
  fontSize: '12px', color: TEXT_BODY, padding: '3px 0',
  borderBottom: `1px solid ${BORDER}`,
},
confirmBar: {
  margin: '0 12px 8px', padding: '10px 12px',
  backgroundColor: '#EAF2FB',
  border: `1px solid ${ACCENT_B}`,
  borderRadius: '6px',
  display: 'flex', flexDirection: 'column', gap: '8px',
},
confirmBarText: {
  fontSize: '13px', color: TEXT_BODY, lineHeight: 1.5, fontFamily: FONT,
},
confirmBarLabel: {
  fontWeight: 600, marginRight: '4px', color: ACCENT,
},
confirmBarTranscription: {
  fontStyle: 'italic',
},
confirmBarButtons: {
  display: 'flex', gap: '6px', justifyContent: 'flex-end',
},
confirmBarCancel: {
  fontSize: '12px', padding: '5px 12px', borderRadius: '4px',
  border: `1px solid ${BORDER}`, backgroundColor: 'transparent',
  color: TEXT_MID, cursor: 'pointer', fontFamily: FONT,
},
confirmBarSend: {
  fontSize: '12px', padding: '5px 14px', borderRadius: '4px',
  border: 'none', backgroundColor: ACCENT,
  color: '#fff', cursor: 'pointer', fontFamily: FONT, fontWeight: 600,
},
};

// ─── Component ────────────────────────────────────────────────────────────────
class AgentDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeCategory: 'All',
      viewMode: 'compact',
      activeAgent: null,
      browsingAgents: false,
      messages: [],
      inputText: '',
      loading: false,
      loadingHistory: false,
      blocked: false,
      blockReason: null,
      blockMessage: '',
      mobile: isMobile(),
      showUnsubscribeConfirm: false,
      unsubscribing: false,
      enterToSend: false, 
      selectedFile: null, 
      uploadingFile: false, 
      uploadProgress: 0,
      fileError: null,
      connectedAgents: [], 
      showConnectedAgents: false,
      isRecording: false,
      transcribing: false,
      recordingDuration: 0,
      recordingMode: null, // 'voice' | 'dictate' | null
      showScrollTop: false,
      showVoiceModal: false,
      currentVoice: 'alloy',       // what's actually saved
      defaultVoice: 'alloy',       // the agent's default (for the "reset" option)
      selectedVoice: 'alloy',      // staged selection in the modal
      savingVoice: false,
      previewingVoice: null,       // which voice is currently playing
      showMathKeyboard: false,
      showFocusModal:   false,
      lcFallbackLoading: false,
      lcFallbackError:   null,
      blockData: null, 
      responseDelayEnabled: true,
      responseDelayMaxSeconds: 10,
      avatarLoadErrors: {},
      showTypingIndicator: false,
    };

    this.messagesEndRef = React.createRef();
    this.textareaRef    = React.createRef();
    this.fileInputRef = React.createRef();
    this.messagesRef = React.createRef();
    this.focusTextareaRef = React.createRef();
    this.statsRef = React.createRef();
    this.audioChunks = [];
    this.mediaRecorder = null;
    this.recordingTimer = null;
    this.audioPlayer = null;
    this.previewPlayer = null;
    this._delayTimer = null;
  }

  // componentDidMount() {
  //   this.handleResize = () => this.setState({ mobile: isMobile() });
  //   window.addEventListener('resize', this.handleResize);
  // }

async componentDidMount() {
  this.handleResize = () => this.setState({ mobile: isMobile() });
  window.addEventListener('resize', this.handleResize);
  this._unmounted = false;
  this.handleVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    const a = this.state.activeAgent;
    if (a && this.readPendingSend(a.id)) this.startReplyRecovery(a.id);
  };
  document.addEventListener('visibilitychange', this.handleVisibility);

  document.addEventListener('mousedown', this.handleDocumentClick);
  this.loadConnectedAgents();

  // ---- dry run clean FCM notifications /////////////////////////////
  //     try {
  //   const res = await axios.post(`${FB_URL}/m175-cleanupDesktopFcmTokens`, { params: { dryRun: true  } });
  //   const data = res.data || {};
  //   if (data.success) {
  //     console.log('cleanupDesktopFcmTokens: ', data);
  //   }
  // } catch (e) {
  //   console.error('AgentDashboard: connected-agents load error:', e);
  // }
 //////////////////////////////////////////////////////////////////////

  // ---- Auto-open agent from URL parameter (e.g. from a push notification) ----
  // Falls back to Dennis as the default landing agent when no URL param is given.
  const agentIdFromUrl = this.props.match?.params?.agentId;
  if (agentIdFromUrl) {
    const matchedAgent = ALL_AGENTS.find(a => a.id === agentIdFromUrl);
    if (matchedAgent) {
      this.openAgent(matchedAgent);
    }
  } else {
    this.openDefaultAgent();
  }
    const saved = localStorage.getItem('agentEnterToSend');
  if (saved !== null) {
    this.setState({ enterToSend: saved === 'true' });
  }
}

componentWillUnmount() {
  window.removeEventListener('resize', this.handleResize);
  document.removeEventListener('mousedown', this.handleDocumentClick);
  this._unmounted = true;
  document.removeEventListener('visibilitychange', this.handleVisibility);
  if (this._recoverTimer) clearTimeout(this._recoverTimer);

  if (this.recordingTimer) clearInterval(this.recordingTimer);
  if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
    this.mediaRecorder.stop();
  }
  if (this.audioPlayer) {
    this.audioPlayer.pause();
    this.audioPlayer = null;
  }
  if (this.previewPlayer) {
    try { this.previewPlayer.pause(); } catch (_) {}
    this.previewPlayer = null;
  }
  // Safety: ensure body scroll is released even if modal didn't close cleanly
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';

  if (this._delayTimer) { clearTimeout(this._delayTimer); this._delayTimer = null; }
}

  componentDidUpdate(_, prevState) {
    if (prevState.enterToSend !== this.state.enterToSend) {
      localStorage.setItem('agentEnterToSend', this.state.enterToSend);
    }
    if (
      prevState.messages.length !== this.state.messages.length ||
      prevState.loading !== this.state.loading
    ) {
      this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }

loadConnectedAgents = async () => {
  const { user } = this.props;
  const uid = user?.uid || user?.id;
  // console.log('AgentDashboard user 1:', user);
  // //console.log('AgentDashboard userdata 1:', userdata);
  // console.log('AgentDashboard user 2:', this.props.user);

  if (!uid) return;
  try {
    const res = await axios.post(`${FB_URL}/m162-getConnectedAgents`, { params: { uid } });
    const data = res.data || {};
    if (data.success) {
      this.setState({ connectedAgents: data.agents || [] });
    }
  } catch (e) {
    console.error('AgentDashboard: connected-agents load error:', e);
  }
};

openDefaultAgent = () => {
  const dennis = ALL_AGENTS.find(a => a.id === 'dennis');
  if (dennis) this.openAgent(dennis);
};

openAgent = async (agent) => {
    const { user } = this.props;
    const uid = user?.uid || user?.id;
   this.setState({
      activeAgent: agent, messages: [], inputText: '',
      blocked: false, blockReason: null, blockMessage: '', loadingHistory: true,
      loading: false, // guard against a stuck loading flag from a previous agent
      browsingAgents: false, // opening any agent always returns to full-focus chat view
    }, this.resetTextareaHeight);
    try {
      const res = await axios.post(`${FB_URL}/m150-agentHistory`, {
        params: { agentId: agent.id, uid },
      });
      const data = res.data || {};
      let messages = data.success && data.conversationHistory?.length > 0
        ? data.conversationHistory
        : [];

      // Resolve Storage paths to download URLs for any voice messages
      const audioMsgs = messages.map((m, i) => ({ m, i })).filter(({ m }) => m.audioPath);
      if (audioMsgs.length > 0) {
        const urls = await Promise.all(
          audioMsgs.map(({ m }) =>
            firebase.storage.ref(m.audioPath).getDownloadURL().catch(() => null)
          )
        );
        messages = messages.map((m, i) => {
          const found = audioMsgs.findIndex(x => x.i === i);
          return found >= 0 ? { ...m, audioUrl: urls[found] } : m;
        });
      }

    const delay = data.responseDelay || {};
    const commonDelayState = {
      responseDelayEnabled: delay.enabled !== false,
      responseDelayMaxSeconds: typeof delay.maxSeconds === 'number' ? delay.maxSeconds : 10,
    };

    if (data.success && data.unsubscribed) {
      this.setState({
        messages,
        loadingHistory: false,
        blocked: true,
        blockReason: 'unsubscribed',
        blockMessage: `You have unsubscribed from ${agent.label}. Resubscribe to continue your conversation.`,
        currentVoice: data.voicePreference || data.defaultVoice || 'alloy',
        defaultVoice: data.defaultVoice || 'alloy',
        ...commonDelayState,
      });
    } else if (data.success && data.blocked) {
      this.setState({
        messages,
        loadingHistory: false,
        blocked: true,
        blockReason: data.reason,
        blockMessage: data.message || 'This agent requires a higher pledge level.',
        blockData: data.reason === 'insufficient_tokens' ? {
          baseTokenCost:     data.baseTokenCost     || 0,
          lcCostWithPremium: data.lcCostWithPremium || 0,
          livePoints:        data.livePoints        || 0,
          canUseLc:          data.canUseLc          || false,
          lcPremiumPct:      data.lcPremiumPct      || 20,
        } : null,
        currentVoice: data.voicePreference || data.defaultVoice || 'alloy',
        defaultVoice: data.defaultVoice || 'alloy',
        ...commonDelayState,
      });
    } else {
      this.setState({
        messages,
        loadingHistory: false,
        currentVoice: data.voicePreference || data.defaultVoice || 'alloy',
        defaultVoice: data.defaultVoice || 'alloy',
        ...commonDelayState,
      });
    }
    if (this.readPendingSend(agent.id)) this.startReplyRecovery(agent.id);
    } catch (e) {
      console.error('AgentDashboard: history load error:', e);
      this.setState({ loadingHistory: false });
    }
    setTimeout(() => this.textareaRef.current?.focus(), 150);
  };

  // ---- Reply recovery -------------------------------------------------------
  // The server finishes and SAVES a reply even if this page lost the HTTP response
  // (phone suspended the page, network dropped, page reloaded mid-request). These
  // methods notice that a send was still pending and pull the saved reply from
  // m150-agentHistory. The marker lives in localStorage so it survives a page reload.
  markPendingSend = (agentId, text) => {
    try { localStorage.setItem('agentPendingSend', JSON.stringify({ agentId, text, at: Date.now() })); } catch (_) {}
  };

  clearPendingSend = () => {
    try { localStorage.removeItem('agentPendingSend'); } catch (_) {}
  };

  readPendingSend = (agentId) => {
    try {
      const p = JSON.parse(localStorage.getItem('agentPendingSend') || 'null');
      if (!p || p.agentId !== agentId || !p.text || Date.now() - p.at > 240000) return null;
      return p;
    } catch (_) { return null; }
  };

  // Returns true when there is nothing (left) to recover.
  recoverPendingReply = async (agentId) => {
    const pending = this.readPendingSend(agentId);
    if (!pending) return true;
    const { user } = this.props;
    const uid = user?.uid || user?.id;
    try {
      const res = await axios.post(`${FB_URL}/m150-agentHistory`, { params: { agentId, uid } });
      const data = res.data || {};
      let serverMsgs = data.success && Array.isArray(data.conversationHistory) ? data.conversationHistory : [];

      // Has the server saved the reply to the message that was pending?
      const from = Math.max(0, serverMsgs.length - 6);
      const saved = serverMsgs.some((m, i) =>
        i >= from && m.role === 'user' && typeof m.content === 'string' &&
        m.content.includes(pending.text) &&
        serverMsgs.slice(i + 1).some(x => x.role === 'assistant')
      );
      if (!saved) return false;

      // Resolve Storage paths to download URLs for any voice messages (same as openAgent)
      const audioMsgs = serverMsgs.map((m, i) => ({ m, i })).filter(({ m }) => m.audioPath);
      if (audioMsgs.length > 0) {
        const urls = await Promise.all(
          audioMsgs.map(({ m }) =>
            firebase.storage.ref(m.audioPath).getDownloadURL().catch(() => null)
          )
        );
        serverMsgs = serverMsgs.map((m, i) => {
          const found = audioMsgs.findIndex(x => x.i === i);
          return found >= 0 ? { ...m, audioUrl: urls[found] } : m;
        });
      }

      if (this._unmounted || this.state.activeAgent?.id !== agentId) return true;
      // The reply is in. If the original request is still hanging, it is now obsolete.
      this._sendSuperseded = true;
      this.clearPendingSend();
      this.setState({ messages: serverMsgs, loading: false, showTypingIndicator: false });
      playPopSound();
      return true;
    } catch (e) {
      console.error('AgentDashboard: reply recovery error:', e);
      return false;
    }
  };

  // Polls every 4 s (up to 15 times) until the saved reply shows up.
  startReplyRecovery = (agentId) => {
    if (this._recovering) return;
    this._recovering = true;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const done = await this.recoverPendingReply(agentId);
      const stillHere = !this._unmounted && this.state.activeAgent?.id === agentId;
      if (done || tries >= 40 || !stillHere) {
        this._recovering = false;
        if (!done && tries >= 40) this.clearPendingSend();
        return;
      }
      this._recoverTimer = setTimeout(tick, 4000);
    };
    this._recoverTimer = setTimeout(tick, 3000);
  };

  // closeChat = () => {
  //   this.setState({ activeAgent: null, messages: [], inputText: '', blocked: false });
  // };

  closeChat = () => {
  if (this._sendTimer) { clearTimeout(this._sendTimer); this._sendTimer = null; }
  if (this._delayTimer) { clearTimeout(this._delayTimer); this._delayTimer = null; }  // ← add
  this.setState({
      activeAgent: null,
      messages: [],
      inputText: '',
      blocked: false,
      deferredSending: false,
      showTypingIndicator: false,
      loading: false, // guard against a stuck loading flag from an in-flight request
        }, this.resetTextareaHeight);
      };

handleUnsubscribeClick = () => {
  this._scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${this._scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  this.setState({ showUnsubscribeConfirm: true });
};

scrollToTop = () => {
  this.messagesRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
};

confirmUnsubscribe = async () => {
  const { activeAgent } = this.state;
  const { user } = this.props;
  const uid = user?.uid || user?.id;
  this.setState({ unsubscribing: true });
  try {
    const res = await axios.post(`${FB_URL}m156-agentunsubscribe`, {
      params: { agentId: activeAgent.id, uid, action: 'unsubscribe' },
    });
    if (res.data && res.data.success === true) {
      console.log('agentUnsubscribe success!');
    } else {
      console.log('agentUnsubscribe error: ', res.data);
    }
  } catch (e) {
    console.error('Unsubscribe error:', e.message);
  }
  this.setState({ showUnsubscribeConfirm: false, unsubscribing: false });
  // Release body lock before closing chat
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, this._scrollY || 0);
  this.closeChat();
};

handleLcFallback = async () => {
  const { activeAgent } = this.state;
  const { user } = this.props;
  const uid = user?.uid || user?.id;
  if (!uid || !activeAgent) return;

  this.setState({ lcFallbackLoading: true, lcFallbackError: null });

  try {
    const res = await axios.post(`${FB_URL}m165-settleFromLc`, {
      params: { agentId: activeAgent.id, uid },
    });
    const data = res.data || {};

    if (data.success) {
      // Settlement succeeded — unblock the conversation
      this.setState({
        blocked:          false,
        blockReason:      null,
        blockMessage:     '',
        blockData:        null,
        lcFallbackLoading: false,
        lcFallbackError:  null,
      });
      // Show a brief confirmation in the message list
      this.setState(prev => ({
        messages: [...prev.messages, {
          role: 'assistant',
          content:
            `${(data.lcDeducted / 100).toFixed(2)} Lc used to fund this session. ` +
            `You have ${(data.pointsRemaining / 100).toFixed(2)} Lc remaining. ` +
            `To avoid using your Lc rewards, buy tokens with MEV from the ThinkLead exchange.`,
        }],
      }));
    } else if (data.insufficientLc) {
      this.setState({
        lcFallbackLoading: false,
        lcFallbackError:   data.message || 'Not enough Lc to cover this.',
      });
    } else {
      throw new Error(data.error || 'Lc fallback failed');
    }
  } catch (e) {
    console.error('handleLcFallback error:', e.message);
    this.setState({
      lcFallbackLoading: false,
      lcFallbackError:   'Something went wrong. Please try again.',
    });
  }
};

confirmResubscribe = async () => {
  const { activeAgent } = this.state;
  const { user } = this.props;
  const uid = user?.uid || user?.id;
  this.setState({ unsubscribing: true });
  try {
    const res = await axios.post(`${FB_URL}m156-agentunsubscribe`, {
      params: { agentId: activeAgent.id, uid, action: 'resubscribe' },
    });
    if (res.data && res.data.success === true) {
      console.log('agentResubscribe success!');
      this.setState({ blocked: false, blockReason: null, blockMessage: '' });
    }
  } catch (e) {
    console.error('Resubscribe error:', e.message);
  }
  this.setState({ unsubscribing: false });
};

cancelUnsubscribe = () => {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, this._scrollY || 0);
  this.setState({ showUnsubscribeConfirm: false });
};

handleMessagesScroll = (e) => {
  const scrollTop = e.target.scrollTop;
  // Show the button only after the user has scrolled down >200px from the top
  const shouldShow = scrollTop > 200;
  if (shouldShow !== this.state.showScrollTop) {
    this.setState({ showScrollTop: shouldShow });
  }
};

handleDocumentClick = (e) => {
  if (
    this.state.showConnectedAgents &&
    this.statsRef.current &&
    !this.statsRef.current.contains(e.target)
  ) {
    this.setState({ showConnectedAgents: false });
  }
};

handleKeyDown = (e) => {
  if (e.key === 'Enter') {
    if (this.state.enterToSend && !e.shiftKey) {
      e.preventDefault();
      if (this.state.inputText.trim() || this.state.selectedFile) {
        this.sendMessage();
      }
      // else: empty textarea + Enter = do nothing (don't fire mic accidentally)
    } 
  }
};

handleInputChange = (e) => {
  const value = e.target.value;
  this.setState({ inputText: value });
  // Resize whichever textarea fired the event — works for both
  // the main panel (textareaRef) and the focus modal (focusTextareaRef)
  const textarea = e.target;
  textarea.style.height = 'auto';
  textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
};

  // ── Agent icon — renders a custom image avatar when the agent defines one
  // (agent.avatar), falling back to the emoji in agent.icon otherwise.
  renderAgentIcon = (agent, sizePx, extraStyle = {}) => {
    const failed = this.state.avatarLoadErrors[agent.id];
    if (agent.avatar && !failed) {
      return (
        <span
          style={{
            width: sizePx, height: sizePx, borderRadius: '50%',
            overflow: 'hidden', flexShrink: 0, display: 'block',
            ...extraStyle,
          }}
        >
          <img
            src={agent.avatar}
            alt=""
            onError={() => this.setState(prev => ({
              avatarLoadErrors: { ...prev.avatarLoadErrors, [agent.id]: true },
            }))}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </span>
      );
    }
    return <span style={{ fontSize: sizePx, lineHeight: 1, flexShrink: 0, ...extraStyle }}>{agent.icon}</span>;
  };

resetTextareaHeight = () => {
  [this.textareaRef, this.focusTextareaRef].forEach(ref => {
    const ta = ref.current;
    if (ta) {
      ta.style.height = '44px'; // matches S.textarea's minHeight — one line
    }
  });
};

handleFileSelected = (e) => {
  const file = e.target.files?.[0];
  e.target.value = ''; // lets the user pick the same file again later if they cancel

  if (!file) return;

  if (file.type !== 'application/pdf') {
    this.setState({ fileError: 'Only PDF files are supported right now.' });
    return;
  }
  if (file.size > 15 * 1024 * 1024) {
    this.setState({ fileError: 'File is too large — please keep it under 15MB.' });
    return;
  }

  this.setState({ selectedFile: file, fileError: null });
};

clearSelectedFile = () => this.setState({ selectedFile: null, fileError: null });

handleAttachClick = () => this.fileInputRef.current?.click();

uploadSelectedFile = (uid, agentId, file) => {
  return new Promise((resolve, reject) => {
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `agentUploads/${uid}/${agentId}/${timestamp}-${safeName}`;

    // uploadSelectedFile, line ~556-560
    const uploadTask = firebase.storage.ref(storagePath).put(file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        this.setState({ uploadProgress: progress });
      },
      (error) => reject(error),
      () => resolve({ storagePath, fileName: file.name })
    );
  });
};

// ── Voice: recording ───────────────────────────────────────────────────────
startRecording = async (mode = 'voice') => {
  // Guard against double-starts — happened in mobile testing when a previous recording didn't clean up
  if (this.state.isRecording || (this.mediaRecorder && this.mediaRecorder.state !== 'inactive')) {
    console.warn('startRecording: a recording is already in progress; stopping it first');
    this.stopRecording();
    return;
  }
  if (!navigator.mediaDevices || !window.MediaRecorder) {
    alert('Voice recording is not supported in this browser.');
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

// Try formats in order of preference. iOS Safari only supports audio/mp4.
// Some Android Chrome versions return true for webm but reject the codec — we test by trying to instantiate.
const candidates = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  '',  // last resort: let the browser pick
];
let mimeType = '';
for (const c of candidates) {
  if (c === '' || (window.MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c))) {
    mimeType = c;
    break;
  }
}
console.log('startRecording: selected mimeType =', mimeType || '(browser default)');

    this.audioChunks = [];
  this.mediaRecorder = mimeType
  ? new MediaRecorder(stream, { mimeType })
  : new MediaRecorder(stream);
// Capture the actual mime the recorder chose (may differ from what we asked)
const actualMime = this.mediaRecorder.mimeType || mimeType || 'audio/webm';
console.log('startRecording: actual recorder mimeType =', actualMime);

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.audioChunks.push(e.data);
    };

    this.mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop());
    const blob = new Blob(this.audioChunks, { type: actualMime });
      this.audioChunks = [];
      if (blob.size < 1500) {
        this.setState({ isRecording: false, recordingMode: null, recordingDuration: 0 });
        return; // too short — ignore
      }
     const capturedDuration = this.state.recordingDuration;
     this.setState({ isRecording: false, recordingDuration: 0, transcribing: true });
     this.uploadAudioForTranscription(blob, mimeType, capturedDuration);
    };

    this.mediaRecorder.start();
    this.setState({ isRecording: true, recordingMode: mode, recordingDuration: 0 });

    const startTime = Date.now();
    this.recordingTimer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      this.setState({ recordingDuration: elapsed });
      if (elapsed >= 60) this.stopRecording(); // safety cap at 60s
    }, 250);
} catch (error) {
  console.error('Mic access error:', error);
  // Show the actual error name on mobile where the console isn't accessible
  const reason = error?.name || error?.message || 'unknown error';
  alert(`Microphone error: ${reason}\n\nMake sure you've allowed microphone access for this site.`);
  this.setState({ isRecording: false, recordingMode: null });
}
};

stopRecording = () => {
  if (this.recordingTimer) { clearInterval(this.recordingTimer); this.recordingTimer = null; }
  if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
    this.mediaRecorder.stop();
  }
};

uploadAudioForTranscription = async (blob, mimeType, audioDurationSeconds = 0) => {
  const { user } = this.props;
  const { activeAgent, recordingMode } = this.state;
  const uid = user?.uid || user?.id;
  if (!uid || !activeAgent) {
    this.setState({ transcribing: false, recordingMode: null });
    return;
  }

  try {
    const ext = mimeType.includes("mp4") ? "m4a" : "webm";
    const formData = new FormData();
    formData.append("uid", uid);
    formData.append("file", blob, `voice.${ext}`);
    formData.append("agentId", activeAgent.id);
    // Only persist audio when this is a voice-note (WhatsApp) recording
    if (recordingMode === 'voice') {
      formData.append("storeAudio", "true");
    }
    // Pass user's preferred language if set, so Whisper transcribes correctly.
    // If unset, backend defaults to English.
    const userLang = this.props.user?.preferredLanguage;
    if (userLang && typeof userLang === 'string' && userLang.length === 2) {
      formData.append("language", userLang);
    }

    const res = await axios.post(`${FB_URL}/m158-transcribeVoice`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });

    const data = res.data || {};
    this.setState({ transcribing: false });

    if (!data.success || !data.text) {
      this.setState({ recordingMode: null });
      if (data.success && !data.text) alert("Didn't catch anything — try again?");
      else alert("Transcription failed. Please try again.");
      return;
    }

    if (recordingMode === 'dictate') {
      // Drop into the textarea for the user to review and send manually
      this.setState(prev => {
        const existing = (prev.inputText || '').trim();
        const newText = existing ? `${existing} ${data.text}` : data.text;
        return { inputText: newText, recordingMode: null };
      }, () => {
        // Auto-grow textarea after the React update
        const ta = this.textareaRef.current;
        if (ta) {
          ta.style.height = 'auto';
          ta.style.height = ta.scrollHeight + 'px';
          ta.focus();
        }
      });
    } else {
      // 'voice' — send straight through as an audio bubble
      const localAudioUrl = URL.createObjectURL(blob);
      this.setState({ recordingMode: null });
      this.sendVoiceMessage(data.text, data.audioPath, localAudioUrl, audioDurationSeconds);
    }
  } catch (error) {
    console.error("Transcription error:", error);
    this.setState({ transcribing: false, recordingMode: null });
    alert("Transcription failed. Please try again.");
  }
};

// Mic — tap to toggle on both touch and mouse
handleMicPointerDown = (e) => {
  if (e.button && e.button !== 0) return;
  e.preventDefault();
  // Toggle: if any recording is in progress, stop it; else start voice recording
  if (this.state.isRecording) {
    this.stopRecording();
  } else {
    this.startRecording('voice');
  }
};

// Dictate — tap to toggle, same pattern
handleDictatePointerDown = (e) => {
  if (e.button && e.button !== 0) return;
  e.preventDefault();
  if (this.state.isRecording) {
    this.stopRecording();
  } else {
    this.startRecording('dictate');
  }
};

// Mic — hold-to-talk on touch, tap-to-toggle on mouse
// handleMicPointerDown = (e) => {
//   alert(`Mic pointerdown fired. pointerType=${e.pointerType}, button=${e.button}`);
//   if (e.button && e.button !== 0) return;
//   e.currentTarget.setPointerCapture?.(e.pointerId);
//   this._micPressedAt = Date.now();
//   this._micPointerType = e.pointerType;
//   if (e.pointerType === 'touch') {
//     this.startRecording('voice');
//   }
// };

// handleMicPointerUp = (e) => {
//   if (this._micPointerType === 'touch') {
//     // Release ends the recording
//     this.stopRecording();
//   } else {
//     // Mouse: tap-to-toggle
//     if (this.state.isRecording) this.stopRecording();
//     else this.startRecording('voice');
//   }
//   this._micPointerType = null;
// };

// Dictate — same pattern
// handleDictatePointerDown = (e) => {
//    alert('Dictate pointerdown fired. pointerType=' + e.pointerType);
//   if (e.button && e.button !== 0) return;
//   e.currentTarget.setPointerCapture?.(e.pointerId);
//   this._dictatePointerType = e.pointerType;
//   if (e.pointerType === 'touch') {
//     this.startRecording('dictate');
//   }
// };

// handleDictatePointerUp = (e) => {
//   if (this._dictatePointerType === 'touch') {
//     this.stopRecording();
//   } else {
//     if (this.state.isRecording) this.stopRecording();
//     else this.startRecording('dictate');
//   }
//   this._dictatePointerType = null;
// };

// sendMessage = async (textOverride, wasVoice = false) => {
//   const { inputText, activeAgent, messages, loading, selectedFile } = this.state;
//   const { user } = this.props;
//   // ▼ CHANGED: accept text from the voice confirm bar
//   const useOverride = typeof textOverride === 'string';
//   const text = (useOverride ? textOverride : inputText).trim();
//   const isVoice = useOverride && wasVoice; // captured in closure for the reply
//   // ▲
//   if ((!text && !selectedFile) || !activeAgent || loading) return;
//   const uid = user?.uid || user?.id;
//   if (!uid) return;

//   let attachment = null;

//   if (selectedFile) {
//     this.setState({ uploadingFile: true, uploadProgress: 0, fileError: null });
//     try {
//       attachment = await this.uploadSelectedFile(uid, activeAgent.id, selectedFile);
//     } catch (error) {
//       console.error('AgentDashboard upload error:', error);
//       this.setState({ uploadingFile: false, fileError: 'Upload failed — please try again.' });
//       return;
//     }
//     this.setState({ uploadingFile: false, uploadProgress: 0, selectedFile: null });
//   }

//   this.setState({
//     messages: [...messages, {
//       role: 'user',
//       content: attachment ? `${text}\n\n📎 ${attachment.fileName}`.trim() : text,
//     }],
//     inputText: '', loading: true, blocked: false,
//   });

//   try {
//     const res = await axios.post(`${FB_URL}/m149-agentChat`, {
//       params: { agentId: activeAgent.id, message: text, uid, attachment },
//     });
//     const data = res.data || {};
// if (data.success && data.blocked) {
//       this.setState(prev => ({
//         messages:        prev.messages,
//         loading:         false,
//         blocked:         true,
//         blockReason:     data.reason,
//         blockMessage:    data.message || 'This agent requires a higher pledge level.',
//         blockData:       data.reason === 'insufficient_tokens' ? {
//           baseTokenCost:     data.baseTokenCost     || 0,
//           lcCostWithPremium: data.lcCostWithPremium || 0,
//           livePoints:        data.livePoints        || 0,
//           canUseLc:          data.canUseLc          || false,
//           lcPremiumPct:      data.lcPremiumPct      || 20,
//         } : null,
//         lcFallbackError: null,
//       }));
//       return;
//     }
//     if (data.success && data.reply) {
//       this.setState(prev => ({
//         messages: [...prev.messages, { role: 'assistant', content: data.reply }],
//         loading: false,
//       }));
//       // ▼ CHANGED: match mode — speak the reply if user spoke

//       // ▲
//     } else {
//       throw new Error(data.error || 'No reply received');
//     }
//   // Find:
//   } catch (error) {
//     console.error('AgentDashboard send error:', error);
//     this.setState(prev => ({
//       messages: [...prev.messages, { role: 'assistant', content: 'Something went wrong. Please try again in a moment.' }],
//       loading: false,
//     }));
//   }
// };

sendMessage = async (textOverride, wasVoice = false) => {
  const { inputText, activeAgent, messages, loading, selectedFile, deferredSending,
          responseDelayEnabled, responseDelayMaxSeconds } = this.state;
  const { user } = this.props;
  const useOverride = typeof textOverride === 'string';
  const text = (useOverride ? textOverride : inputText).trim();
  if ((!text && !selectedFile) || !activeAgent || loading || deferredSending) return;
  const uid = user?.uid || user?.userID;
  if (!uid) return;

  let attachment = null;
  if (selectedFile) {
    this.setState({ uploadingFile: true, uploadProgress: 0, fileError: null });
    try {
      attachment = await this.uploadSelectedFile(uid, activeAgent.id, selectedFile);
    } catch (error) {
      console.error('AgentDashboard upload error:', error);
      this.setState({ uploadingFile: false, fileError: 'Upload failed — please try again.' });
      return;
    }
    this.setState({ uploadingFile: false, uploadProgress: 0, selectedFile: null });
  }

  // Show the user's message immediately. Set loading=true (blocks second send),
  // showTypingIndicator=false (silence during the delay window).
  this.setState({
    messages: [...messages, {
      role: 'user',
      content: attachment ? `${text}\n\n📎 ${attachment.fileName}`.trim() : text,
    }],
    inputText: '',
    loading: true,
    showTypingIndicator: false,
    blocked: false,
  });

  // Frontend delay: how long the silence lasts before dots appear.
  const maxMs = responseDelayEnabled
    ? Math.max(0, Math.min(15000, responseDelayMaxSeconds * 1000))
    : 0;
  const delayMs = Math.floor(Math.random() * maxMs);

  ////////////////////////////////////////////////////////////////
  console.log('Response TEXT delay:', {
    responseDelayEnabled,
    responseDelayMaxSeconds,
    maxMs,
    delayMs,
    currentShowTyping: this.state.showTypingIndicator,
  });
  ////////////////////////////////////////////////////////////////

  // Kick off the m149 request AND the delay timer in parallel.
  // The request being in-flight from t=0 means closing the chat during
  // the silence doesn't drop the message.
  const activeAgentIdAtSend = activeAgent.id;

  this._delayTimer = setTimeout(() => {
    console.log('Response DELAY TEXT timer fired, setting showTypingIndicator=true');
    this._delayTimer = null;
    // Only show dots if we're still on the same agent and still waiting
    if (this.state.activeAgent?.id === activeAgentIdAtSend && this.state.loading) {
      this.setState({ showTypingIndicator: true });
    }
  }, delayMs);

  const requestPromise = axios.post(`${FB_URL}/m149-agentChat`, {
    params: { agentId: activeAgent.id, message: text, uid, attachment },
  });

  this._sendSuperseded = false;
  this.markPendingSend(activeAgent.id, text);

  try {
    const res = await requestPromise;
    const data = res.data || {};
    if (this._sendSuperseded) {
      // The saved reply was already pulled in by recovery; do not append it twice.
      this._sendSuperseded = false;
      this.setState({ loading: false, showTypingIndicator: false });
      return;
    }
    this.clearPendingSend(); // the reply arrived normally
    // If reply arrived before the delay elapsed, wait out the remaining delay
    // so the silence period isn't cut short.
    if (this._delayTimer) {
      await new Promise(resolve => {
        const originalTimer = this._delayTimer;
        this._delayTimer = setTimeout(() => {
          clearTimeout(originalTimer);
          this._delayTimer = null;
          resolve();
        }, 0);
        // Wait until the original delay would have fired
      });
    }

    // Handle response
    if (this.state.activeAgent?.id !== activeAgentIdAtSend) {
      // User switched agents while this request was in flight.
      // Still need to clear loading/typing state so it doesn't get stuck
      // stuck 'true' and disable the input box for whatever agent they're on now.
      this.setState({ loading: false, showTypingIndicator: false });
      return;
    }
    if (data.success && data.blocked) {
        this.setState({
          loading: false, showTypingIndicator: false,
          blocked: true,
          blockReason: data.reason,
          blockMessage: data.message || 'This agent requires a higher pledge level.',
          blockData: data.reason === 'insufficient_tokens' ? {
            baseTokenCost:     data.baseTokenCost     || 0,
            lcCostWithPremium: data.lcCostWithPremium || 0,
            livePoints:        data.livePoints        || 0,
            canUseLc:          data.canUseLc          || false,
            lcPremiumPct:      data.lcPremiumPct      || 20,
          } : null,
          lcFallbackError: null,
        });
        return;
      }

    if (data.success && data.reply) {
      this.setState(prev => ({
        messages: [...prev.messages, { role: 'assistant', content: data.reply }],
        loading: false,
        showTypingIndicator: false,
      }));
       playPopSound(); 
    } else {
      throw new Error(data.error || 'No reply received');
    }
   
  } catch (error) {
    console.error('AgentDashboard send error:', error);
    if (this._delayTimer) { clearTimeout(this._delayTimer); this._delayTimer = null; }
    this.setState(prev => ({
      messages: [...prev.messages, { role: 'assistant', content: 'Something went wrong. Please try again in a moment.' }],
      loading: false,
      showTypingIndicator: false,
    }));
    this.startReplyRecovery(activeAgentIdAtSend);
  }
};

sendVoiceMessage = async (transcribedText, userAudioPath, localAudioUrl) => {
  const { activeAgent, messages, loading, deferredSending,
          responseDelayEnabled, responseDelayMaxSeconds } = this.state;

           console.log('AgentDashboard sendVoiceMessage 0: ', activeAgent);

  const { user } = this.props;
  const text = (transcribedText || "").trim();
  if (!text || !activeAgent || loading || deferredSending) return;
  const uid = user?.uid || user?.userID;
  if (!uid) return;

  // Show user's voice bubble immediately
  this.setState({
    messages: [...messages, {
      role: 'user',
      content: text,
      audioPath: userAudioPath,
      audioUrl: localAudioUrl,
      wasVoice: true,
    }],
    loading: true,
    showTypingIndicator: false,
    blocked: false,
  });

  const maxMs = responseDelayEnabled
    ? Math.max(0, Math.min(15000, responseDelayMaxSeconds * 1000))
    : 0;
  const delayMs = Math.floor(Math.random() * maxMs);
  ////////////////////////////////////////////////////////////////
  console.log('Response VOICE delay:', {
    responseDelayEnabled,
    responseDelayMaxSeconds,
    maxMs,
    delayMs,
    currentShowTyping: this.state.showTypingIndicator,
  });
  ////////////////////////////////////////////////////////////////
  const activeAgentIdAtSend = activeAgent.id;

  this._delayTimer = setTimeout(() => {
    console.log('Response DELAY VOICE timer fired, setting showTypingIndicator=true');
    this._delayTimer = null;
    if (this.state.activeAgent?.id === activeAgentIdAtSend && this.state.loading) {
      this.setState({ showTypingIndicator: true });
    }
  }, delayMs);

 console.log('AgentDashboard sendVoiceMessage 1: ', text);

  const requestPromise = axios.post(`${FB_URL}/m149-agentChat`, {
    params: {
      agentId: activeAgent.id, message: text, uid,
      wasVoice: true, userAudioPath,
    },
  });

  this._sendSuperseded = false;
  this.markPendingSend(activeAgent.id, text);

  try {
    const res = await requestPromise;
    const data = res.data || {};

    if (this._sendSuperseded) {
      // The saved reply was already pulled in by recovery; do not append it twice.
      this._sendSuperseded = false;
      this.setState({ loading: false, showTypingIndicator: false });
      return;
    }
    this.clearPendingSend(); // the reply arrived normally

    console.log('AgentDashboard sendVoiceMessage 2: ', data);

  if (this.state.activeAgent?.id !== activeAgentIdAtSend) {
    this.setState({ loading: false, showTypingIndicator: false });
    return;
  }

    if (data.success && data.blocked) {
        this.setState({
          loading: false, showTypingIndicator: false,
          blocked: true,
          blockReason: data.reason,
          blockMessage: data.message || "This agent requires a higher pledge level.",
          blockData: data.reason === 'insufficient_tokens' ? {
            baseTokenCost:     data.baseTokenCost     || 0,
            lcCostWithPremium: data.lcCostWithPremium || 0,
            livePoints:        data.livePoints        || 0,
            canUseLc:          data.canUseLc          || false,
            lcPremiumPct:      data.lcPremiumPct      || 20,
          } : null,
          lcFallbackError: null,
        });
        return;
    }

if (data.success && data.reply) {
  let replyAudioUrl = null;
  if (data.replyAudioPath) {
    try {
      replyAudioUrl = await firebase.storage.ref(data.replyAudioPath).getDownloadURL();
    } catch (e) {
      console.error("Failed to resolve reply audio URL:", e);
    }
  }

  // Only treat this as a voice reply if we actually resolved a playable
  // URL. Previously wasVoice was hardcoded true just because the request
  // was voice — if TTS failed backend-side, or the download URL failed
  // to resolve here, AudioBubble rendered with no audioUrl and got stuck
  // permanently at "Voice note (loading…)" with no fallback to text.
  const hasPlayableAudio = !!replyAudioUrl;

  const audioMissing = !data.replyAudioPath;
  this.setState(prev => ({
    messages: [...prev.messages, {
      role: "assistant",
      content: audioMissing
        ? `🎙 (Audio unavailable this time — here's the text:)\n\n${data.reply}`
        : data.reply,
      audioPath: data.replyAudioPath,
      audioUrl: replyAudioUrl,
      wasVoice: !audioMissing,  // render as text bubble if audio is missing
      autoPlay: !audioMissing,
    }],
    loading: false,
    showTypingIndicator: false,
  }));
    playPopSound(); 
  } else {
    throw new Error(data.error || "No reply received");
  }
  } catch (error) {
    console.error("Voice send error:", error);
    if (this._delayTimer) { clearTimeout(this._delayTimer); this._delayTimer = null; }
    this.setState(prev => ({
      messages: [...prev.messages, { role: "assistant", content: "Something went wrong. Please try again in a moment." }],
      loading: false,
      showTypingIndicator: false,
    }));
    this.startReplyRecovery(activeAgentIdAtSend);
  }
};

// sendVoiceMessage = async (transcribedText, userAudioPath, localAudioUrl) => {
//   const { activeAgent, messages, loading, deferredSending,
//           responseDelayEnabled, responseDelayMaxSeconds } = this.state;

//            console.log('AgentDashboard sendVoiceMessage 0: ', activeAgent);

//   const { user } = this.props;
//   const text = (transcribedText || "").trim();
//   if (!text || !activeAgent || loading || deferredSending) return;
//   const uid = user?.uid || user?.userID;
//   if (!uid) return;

//   // Show user's voice bubble immediately
// this.setState({
//     messages: [...messages, {
//       role: 'user',
//       content: attachment ? `${text}\n\n📎 ${attachment.fileName}`.trim() : text,
//     }],
//     inputText: '',
//     loading: true,
//     showTypingIndicator: false,
//     blocked: false,
//   }, this.resetTextareaHeight);

//   const maxMs = responseDelayEnabled
//     ? Math.max(0, Math.min(15000, responseDelayMaxSeconds * 1000))
//     : 0;
//   const delayMs = Math.floor(Math.random() * maxMs);
//   ////////////////////////////////////////////////////////////////
//   console.log('Response VOICE delay:', {
//     responseDelayEnabled,
//     responseDelayMaxSeconds,
//     maxMs,
//     delayMs,
//     currentShowTyping: this.state.showTypingIndicator,
//   });
//   ////////////////////////////////////////////////////////////////
//   const activeAgentIdAtSend = activeAgent.id;

//   this._delayTimer = setTimeout(() => {
//     console.log('Response DELAY VOICE timer fired, setting showTypingIndicator=true');
//     this._delayTimer = null;
//     if (this.state.activeAgent?.id === activeAgentIdAtSend && this.state.loading) {
//       this.setState({ showTypingIndicator: true });
//     }
//   }, delayMs);

//  console.log('AgentDashboard sendVoiceMessage 1: ', message);

//   const requestPromise = axios.post(`${FB_URL}/m149-agentChat`, {
//     params: {
//       agentId: activeAgent.id, message: text, uid,
//       wasVoice: true, userAudioPath,
//     },
//   });

//   try {
//     const res = await requestPromise;
//     const data = res.data || {};

//     console.log('AgentDashboard sendVoiceMessage 2: ', data);

//     if (this.state.activeAgent?.id !== activeAgentIdAtSend) return;

//     if (data.success && data.blocked) {
//         this.setState({
//           loading: false, showTypingIndicator: false,
//           blocked: true,
//           blockReason: data.reason,
//           blockMessage: data.message || "This agent requires a higher pledge level.",
//           blockData: data.reason === 'insufficient_tokens' ? {
//             baseTokenCost:     data.baseTokenCost     || 0,
//             lcCostWithPremium: data.lcCostWithPremium || 0,
//             livePoints:        data.livePoints        || 0,
//             canUseLc:          data.canUseLc          || false,
//             lcPremiumPct:      data.lcPremiumPct      || 20,
//           } : null,
//           lcFallbackError: null,
//         });
//         return;
//     }

//     if (data.success && data.reply) {
//       let replyAudioUrl = null;
//       if (data.replyAudioPath) {
//         try {
//           replyAudioUrl = await firebase.storage.ref(data.replyAudioPath).getDownloadURL();
//         } catch (e) {
//           console.error("Failed to resolve reply audio URL:", e);
//         }
//       }

//       this.setState(prev => ({
//         messages: [...prev.messages, {
//           role: "assistant",
//           content: data.reply,
//           audioPath: data.replyAudioPath,
//           audioUrl: replyAudioUrl,
//           wasVoice: true,
//           autoPlay: true,
//         }],
//         loading: false,
//         showTypingIndicator: false,
//       }));
//       playPopSound(); 
//     } else {
//       throw new Error(data.error || "No reply received");
//     }
//   } catch (error) {
//     console.error("Voice send error:", error);
//     if (this._delayTimer) { clearTimeout(this._delayTimer); this._delayTimer = null; }
//     this.setState(prev => ({
//       messages: [...prev.messages, { role: "assistant", content: "Something went wrong. Please try again in a moment." }],
//       loading: false,
//       showTypingIndicator: false,
//     }));
//   }
// };

  filteredAgents = () => {
    const { activeCategory } = this.state;
    if (activeCategory === 'All') return ALL_AGENTS;
    return ALL_AGENTS.filter(a => a.category === activeCategory);
  };

  toggleBrowseAgents = () => {
    this.setState(prev => ({ browsingAgents: !prev.browsingAgents }));
  };

openVoiceModal = () => {
  const current = this.state.currentVoice || this.state.defaultVoice || 'alloy';
  // Lock background scroll (iOS-safe pattern)
  this._scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${this._scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  this.setState({
    showVoiceModal: true,
    selectedVoice: current,
  });
};

closeVoiceModal = () => {
  if (this.previewPlayer) {
    try { this.previewPlayer.pause(); } catch (_) {}
    this.previewPlayer = null;
  }
  // Restore scroll
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, this._scrollY || 0);
  this.setState({ showVoiceModal: false, previewingVoice: null });
};

previewVoice = (voiceId) => {
  // Stop any current preview
  if (this.previewPlayer) {
    try { this.previewPlayer.pause(); } catch (_) {}
    this.previewPlayer = null;
  }
  if (this.state.previewingVoice === voiceId) {
    // Same voice tapped again = stop
    this.setState({ previewingVoice: null });
    return;
  }
  const url = VOICE_PREVIEW_URL(voiceId);
  const player = new Audio(url);
  player.onended = () => {
    if (this.state.previewingVoice === voiceId) {
      this.setState({ previewingVoice: null });
    }
  };
  player.onerror = () => {
    console.error('Preview failed for', voiceId);
    this.setState({ previewingVoice: null });
  };
  player.play().catch(e => {
    console.warn('Preview play blocked:', e.message);
    this.setState({ previewingVoice: null });
  });
  this.previewPlayer = player;
  this.setState({ previewingVoice: voiceId });
};

saveVoiceSelection = async () => {
  const { activeAgent, selectedVoice } = this.state;
  const { user } = this.props;
  const uid = user?.uid || user?.id;
  if (!uid || !activeAgent) return;

  this.setState({ savingVoice: true });
  try {
    // const res = await axios.post(`${FB_URL}/m164-setAgentVoice`, {
    //   params: { agentId: activeAgent.id, uid, voice: selectedVoice },
    // });
    const payload = { agentId: activeAgent.id, uid, voice: selectedVoice };
    console.log('saveVoiceSelection payload:', payload);
    const res = await axios.post(`${FB_URL}/m164-setAgentVoice`, { params: payload });
    console.log('saveVoiceSelection response:', res.status, res.data);

    if (res.data?.success) {
      this.setState({
        currentVoice: selectedVoice,
        savingVoice: false,
      });
      this.closeVoiceModal();
    } else {
      throw new Error(res.data?.error || 'Save failed');
    }
} catch (e) {
  console.error('saveVoiceSelection error:', e);
  this.setState({ savingVoice: false });
  // Surface the actual error from the backend so we know what went wrong
  const backendError = e.response?.data?.error;
  const detail = backendError || e.message || 'unknown error';
  alert(`Could not save voice preference:\n\n${detail}\n\nPlease try again or contact support.`);
}
};

  markMessageAutoplayed = (index) => {
    this.setState(prev => ({
      messages: prev.messages.map((m, i) =>
        i === index ? { ...m, autoPlay: false } : m
      ),
    }));
  };

  // ── Expanded card ──────────────────────────────────────────────────────────
  renderCardExpanded = (agent) => {
    const { activeAgent } = this.state;
    const isActive = activeAgent?.id === agent.id;
    return (
      <div
        key={agent.id}
        style={S.cardExpanded(isActive)}
        onClick={() => this.openAgent(agent)}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = ACCENT_B; e.currentTarget.style.boxShadow = '0 2px 8px rgba(26,111,186,0.10)'; }}}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}}
      >
        {isActive && <div style={S.cardGlow} />}
        {this.renderAgentIcon(agent, 26, { marginBottom: '8px', display: 'block' })}
        <div style={S.cardCategory}>{agent.category}</div>
        <div style={S.cardLabel}>{agent.label}</div>
        <div style={S.cardDesc}>{agent.description}</div>
        <button style={S.cardOpenBtn}>{isActive ? '● Active' : 'Open →'}</button>
      </div>
    );
  };

  // ── Compact icon card ──────────────────────────────────────────────────────
  renderCardCompact = (agent) => {
    const { activeAgent } = this.state;
    const isActive = activeAgent?.id === agent.id;
    return (
      <div
        key={agent.id}
        style={S.cardCompact(isActive)}
        onClick={() => this.openAgent(agent)}
        title={agent.label}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = ACCENT_B; e.currentTarget.style.backgroundColor = ACCENT_F; }}}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.backgroundColor = BG_CARD; }}}
      >
        {isActive && <div style={S.activeDot} />}
        {this.renderAgentIcon(agent, 24)}
        <div style={S.cardLabelSm}>{agent.label.split(' ')[0]}</div>
      </div>
    );
  };

  // ── Messages ───────────────────────────────────────────────────────────────

///////////////////////////////////////////////////////////////////////////////
renderBlockedBanner = () => {
  const { blocked, blockReason, blockMessage, blockData, lcFallbackLoading, lcFallbackError, unsubscribing } = this.state;
  if (!blocked) return null;

  let actionElement;

  if (blockReason === 'unsubscribed') {
    // Resubscribe button — unchanged
    actionElement = (
      <button
        onClick={this.confirmResubscribe}
        disabled={unsubscribing}
        style={{
          background: 'none', border: 'none', color: ACCENT,
          textDecoration: 'underline', fontWeight: '600',
          cursor: unsubscribing ? 'not-allowed' : 'pointer',
          fontFamily: FONT, fontSize: '12px', padding: 0,
        }}
      >
        {unsubscribing ? 'Resubscribing…' : 'Resubscribe →'}
      </button>
    );

  } else if (blockReason === 'insufficient_tokens') {
    // Two-option layout: Buy tokens (primary) + Use Lc (fallback)
    actionElement = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>

        {/* Cost summary */}
        {blockData && (
          <div style={{ fontSize: '11px', color: ACCENT, opacity: 0.8, lineHeight: '1.6' }}>
            Agent cost this cycle:{' '}
            <strong>{(blockData.baseTokenCost / 100).toFixed(2)} tokens</strong>
          </div>
        )}

        {/* Primary action: Buy tokens with MEV */}
        <a
          href="https://thinklead.app/mevcoin"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '8px 18px',
            backgroundColor: ACCENT,
            color: '#ffffff',
            borderRadius: '6px',
            textDecoration: 'none',
            fontWeight: '700',
            fontSize: '12px',
            fontFamily: FONT,
          }}
        >
          Buy tokens with MEV →
        </a>

        {/* Divider */}
        <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.5, letterSpacing: '1px' }}>
          OR
        </div>

        {/* Fallback action: Use Lc */}
        {blockData && blockData.canUseLc ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={this.handleLcFallback}
              disabled={lcFallbackLoading}
              style={{
                background: 'none',
                border: '1px solid ' + ACCENT,
                borderRadius: '6px',
                color: ACCENT,
                fontWeight: '600',
                fontSize: '12px',
                fontFamily: FONT,
                padding: '7px 16px',
                cursor: lcFallbackLoading ? 'not-allowed' : 'pointer',
                opacity: lcFallbackLoading ? 0.6 : 1,
              }}
            >
              {lcFallbackLoading
                ? 'Processing...'
                : 'Use ' + (blockData.lcCostWithPremium / 100).toFixed(2) + ' Lc instead (' + blockData.lcPremiumPct + '% premium)'
              }
            </button>
            <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.7, textAlign: 'center', maxWidth: '260px', lineHeight: '1.5' }}>
              You have {(blockData.livePoints / 100).toFixed(2)} Lc available.
              Tokens are the standard way to fund agents. Lc costs {blockData.lcPremiumPct}% more.
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '11px', color: ACCENT, opacity: 0.7, textAlign: 'center', maxWidth: '260px', lineHeight: '1.5' }}>
            {blockData && blockData.livePoints > 0
              ? 'You have ' + (blockData.livePoints / 100).toFixed(2) + ' Lc but need ' + (blockData.lcCostWithPremium / 100).toFixed(2) + ' Lc to use this option.'
              : 'You have no Lc available either. Please buy tokens with MEV to continue.'
            }
          </div>
        )}

        {/* Lc fallback error */}
        {lcFallbackError && (
          <div style={{ fontSize: '11px', color: '#c62828', marginTop: '4px' }}>
            {lcFallbackError}
          </div>
        )}

        {/* Explanation */}
        <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.6, textAlign: 'center', maxWidth: '280px', lineHeight: '1.6', marginTop: '4px' }}>
          Lc is your development reward currency, earned through tests and pledges.
          Buy tokens with MEV to fund your agents at the standard rate and preserve your Lc rewards.
        </div>

      </div>
    );

  } else {
    // Pledge upgrade — unchanged
    actionElement = (
      <a
        href="https://www.patreon.com/mevision"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: ACCENT, textDecoration: 'underline', fontWeight: '600' }}
      >
        Upgrade your pledge →
      </a>
    );
  }

  return (
    <div style={S.blockedBanner}>
      {blockMessage}
      <div style={{ marginTop: '10px' }}>
        {actionElement}
      </div>
    </div>
  );
};
//////////////////////////////////////////////////////////////////////////////

renderMessages = () => {
    const messages = this.state.messages;
    const loading = this.state.loading;
    const loadingHistory = this.state.loadingHistory;
    const blocked = this.state.blocked;
    const blockReason = this.state.blockReason;
    const blockMessage = this.state.blockMessage;
    const activeAgent = this.state.activeAgent;
    const unsubscribing = this.state.unsubscribing;

    if (loadingHistory) {
      return (
        <div style={S.emptyChat}>
          <span style={S.emptyChatIcon}>{activeAgent ? activeAgent.icon : ''}</span>
          <div style={S.emptyChatText}>Loading conversation…</div>
        </div>
      );
    }

if (messages.length === 0 && !loading && !blocked) {
  const welcome = activeAgent ? WELCOME_BY_AGENT[activeAgent.id] : null; // adjust key to your agent id field
  if (!welcome) {
    return (
      <div style={S.emptyChat}>
        <span style={S.emptyChatIcon}>{activeAgent ? activeAgent.icon : ''}</span>
        <div style={S.emptyChatText}>Start the conversation whenever you're ready.</div>
      </div>
    );
  }
  return (
    <div>
      <div style={S.bubble('assistant')}>
        <MathRenderer text={welcome.text} />
      </div>
      <div style={S.bubble('assistant')}>
        <AudioBubble
          audioUrl={welcome.audio}
          isUser={false}
          autoPlay={false}
          onAutoPlayed={() => {}}
        />
      </div>
      <div ref={this.messagesEndRef} />
    </div>
  );
}

const messageBubbles = messages.map((msg, i) => {
  const isUser = msg.role === "user";
  
if (msg.wasVoice || msg.audioPath) {
  const shouldAutoplay = msg.autoPlay === true && !this.state.showFocusModal;
  return (
    <div key={i} style={S.bubble(msg.role)}>
      <AudioBubble
        audioUrl={msg.audioUrl}
        isUser={isUser}
        autoPlay={shouldAutoplay}
        onAutoPlayed={() => this.markMessageAutoplayed(i)}
      />
    </div>
  );
}
  return (
    <div key={i} style={S.bubble(msg.role)}>
      <MathRenderer text={msg.content} />
    </div>
  );
});

const blockedBanner = this.renderBlockedBanner();

// let blockedBanner = null;
// if (blocked) {
//   const { blockData, lcFallbackLoading, lcFallbackError } = this.state;

//   let actionElement;

//   if (blockReason === 'unsubscribed') {
//     // Resubscribe button — unchanged
//     actionElement = (
//       <button
//         onClick={this.confirmResubscribe}
//         disabled={unsubscribing}
//         style={{
//           background: 'none', border: 'none', color: ACCENT,
//           textDecoration: 'underline', fontWeight: '600',
//           cursor: unsubscribing ? 'not-allowed' : 'pointer',
//           fontFamily: FONT, fontSize: '12px', padding: 0,
//         }}
//       >
//         {unsubscribing ? 'Resubscribing…' : 'Resubscribe →'}
//       </button>
//     );

//   } else if (blockReason === 'insufficient_tokens') {
//     // Two-option layout: Buy tokens (primary) + Use Lc (fallback)
//     actionElement = (
//       <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>

//         {/* ── Cost summary ── */}
//         {blockData && (
//           <div style={{ fontSize: '11px', color: ACCENT, opacity: 0.8, lineHeight: '1.6' }}>
//             Agent cost this cycle:{' '}
//             <strong>{(blockData.baseTokenCost / 100).toFixed(2)} tokens</strong>
//           </div>
//         )}

//         {/* ── Primary action: Buy tokens with MEV ── */}
//         <a
//           href="https://thinklead.app/Exchange"
//           target="_blank"
//           rel="noopener noreferrer"
//           style={{
//             display: 'inline-block',
//             padding: '8px 18px',
//             backgroundColor: ACCENT,
//             color: '#ffffff',
//             borderRadius: '6px',
//             textDecoration: 'none',
//             fontWeight: '700',
//             fontSize: '12px',
//             fontFamily: FONT,
//           }}
//         >
//           Buy tokens with MEV →
//         </a>

//         {/* ── Divider ── */}
//         <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.5, letterSpacing: '1px' }}>
//           — OR —
//         </div>

//         {/* ── Fallback action: Use Lc ── */}
//         {blockData?.canUseLc ? (
//           <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
//             <button
//               onClick={this.handleLcFallback}
//               disabled={lcFallbackLoading}
//               style={{
//                 background: 'none',
//                 border: `1px solid ${ACCENT}`,
//                 borderRadius: '6px',
//                 color: ACCENT,
//                 fontWeight: '600',
//                 fontSize: '12px',
//                 fontFamily: FONT,
//                 padding: '7px 16px',
//                 cursor: lcFallbackLoading ? 'not-allowed' : 'pointer',
//                 opacity: lcFallbackLoading ? 0.6 : 1,
//               }}
//             >
//               {lcFallbackLoading
//                 ? 'Processing…'
//                 : `Use ${(blockData.lcCostWithPremium / 100).toFixed(2)} Lc instead (${blockData.lcPremiumPct}% premium)`
//               }
//             </button>
//             <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.7, textAlign: 'center', maxWidth: '260px', lineHeight: '1.5' }}>
//               You have {(blockData.livePoints / 100).toFixed(2)} Lc available.
//               Tokens are the standard way to fund agents — Lc costs {blockData.lcPremiumPct}% more.
//             </div>
//           </div>
//         ) : (
//           <div style={{ fontSize: '11px', color: ACCENT, opacity: 0.7, textAlign: 'center', maxWidth: '260px', lineHeight: '1.5' }}>
//             {blockData?.livePoints > 0
//               ? `You have ${(blockData.livePoints / 100).toFixed(2)} Lc but need ${(blockData.lcCostWithPremium / 100).toFixed(2)} Lc to use this option.`
//               : 'You have no Lc available either. Please buy tokens with MEV to continue.'
//             }
//           </div>
//         )}

//         {/* ── Lc fallback error ── */}
//         {lcFallbackError && (
//           <div style={{ fontSize: '11px', color: '#c62828', marginTop: '4px' }}>
//             {lcFallbackError}
//           </div>
//         )}

//         {/* ── Explanation ── */}
//         <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.6, textAlign: 'center', maxWidth: '280px', lineHeight: '1.6', marginTop: '4px' }}>
//           Lc is your development reward currency — earned through tests and pledges.
//           Buy tokens with MEV to fund your agents at the standard rate and preserve your Lc rewards.
//         </div>

//       </div>
//     );

//   } else {
//     // Pledge upgrade — unchanged
//     actionElement = (
//       <a
//         href="https://www.patreon.com/mevision"
//         target="_blank"
//         rel="noopener noreferrer"
//         style={{ color: ACCENT, textDecoration: 'underline', fontWeight: '600' }}
//       >
//         Upgrade your pledge →
//       </a>
//     );
//   }

//   blockedBanner = (
//     <div style={S.blockedBanner}>
//       {blockMessage}
//       <div style={{ marginTop: '10px' }}>
//         {actionElement}
//       </div>
//     </div>
//   );
// }

    return (
      <div>
        {messageBubbles}
        {loading && this.state.showTypingIndicator ? <TypingIndicator /> : null}      
        {blockedBanner}
        <div ref={this.messagesEndRef} />
      </div>
    );
  };
  
// renderMessages_bak = () => {
//     const messages = this.state.messages;
//     const loading = this.state.loading;
//     const loadingHistory = this.state.loadingHistory;
//     const blocked = this.state.blocked;
//     const blockReason = this.state.blockReason;
//     const blockMessage = this.state.blockMessage;
//     const activeAgent = this.state.activeAgent;
//     const unsubscribing = this.state.unsubscribing;

//     if (loadingHistory) {
//       return (
//         <div style={S.emptyChat}>
//           <span style={S.emptyChatIcon}>{activeAgent ? activeAgent.icon : ''}</span>
//           <div style={S.emptyChatText}>Loading conversation…</div>
//         </div>
//       );
//     }

//     if (messages.length === 0 && !loading && !blocked) {
//       return (
//         // <div style={S.emptyChat}>
//         //   <span style={S.emptyChatIcon}>{activeAgent ? activeAgent.icon : ''}</span>
//         //   <div style={S.emptyChatText}>
//         //     Begin your conversation.
//         //     <br />
//         //     Every question draws wisdom out.
//         //   </div>
//         // </div>
// <div
//   style={{
//     width: '100%',
//     display: 'flex',
//     justifyContent: 'center',
//     alignItems: 'center',
//     padding: '24px 16px',
//     boxSizing: 'border-box'
//   }}
// >
//   <video
//     key={videourl}
//     src={videourl}
//     width="600"
//     autoPlay
//     muted
//     loop
//     playsInline
//     preload="auto"
//     controls
//     onError={(e) => console.error('Dennis video error:', e.currentTarget.error)}
//     style={{
//       display: 'block',
//       width: '100%',
//       maxWidth: '600px',
//       height: 'auto',
//       maxHeight: '400px',
//       objectFit: 'contain',
//       borderRadius: '12px',
//       backgroundColor: 'transparent'
//     }}
//   >
//     Your browser does not support HTML video.
//   </video>
// </div>
//       );
//     }

// const messageBubbles = messages.map((msg, i) => {
//   const isUser = msg.role === "user";
  
// if (msg.wasVoice || msg.audioPath) {
//   const shouldAutoplay = msg.autoPlay === true && !this.state.showFocusModal;
//   return (
//     <div key={i} style={S.bubble(msg.role)}>
//       <AudioBubble
//         audioUrl={msg.audioUrl}
//         isUser={isUser}
//         autoPlay={shouldAutoplay}
//         onAutoPlayed={() => this.markMessageAutoplayed(i)}
//       />
//     </div>
//   );
// }
//   return (
//     <div key={i} style={S.bubble(msg.role)}>
//       <MathRenderer text={msg.content} />
//     </div>
//   );
// });

// const blockedBanner = this.renderBlockedBanner();

// // let blockedBanner = null;
// // if (blocked) {
// //   const { blockData, lcFallbackLoading, lcFallbackError } = this.state;

// //   let actionElement;

// //   if (blockReason === 'unsubscribed') {
// //     // Resubscribe button — unchanged
// //     actionElement = (
// //       <button
// //         onClick={this.confirmResubscribe}
// //         disabled={unsubscribing}
// //         style={{
// //           background: 'none', border: 'none', color: ACCENT,
// //           textDecoration: 'underline', fontWeight: '600',
// //           cursor: unsubscribing ? 'not-allowed' : 'pointer',
// //           fontFamily: FONT, fontSize: '12px', padding: 0,
// //         }}
// //       >
// //         {unsubscribing ? 'Resubscribing…' : 'Resubscribe →'}
// //       </button>
// //     );

// //   } else if (blockReason === 'insufficient_tokens') {
// //     // Two-option layout: Buy tokens (primary) + Use Lc (fallback)
// //     actionElement = (
// //       <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>

// //         {/* ── Cost summary ── */}
// //         {blockData && (
// //           <div style={{ fontSize: '11px', color: ACCENT, opacity: 0.8, lineHeight: '1.6' }}>
// //             Agent cost this cycle:{' '}
// //             <strong>{(blockData.baseTokenCost / 100).toFixed(2)} tokens</strong>
// //           </div>
// //         )}

// //         {/* ── Primary action: Buy tokens with MEV ── */}
// //         <a
// //           href="https://thinklead.app/Exchange"
// //           target="_blank"
// //           rel="noopener noreferrer"
// //           style={{
// //             display: 'inline-block',
// //             padding: '8px 18px',
// //             backgroundColor: ACCENT,
// //             color: '#ffffff',
// //             borderRadius: '6px',
// //             textDecoration: 'none',
// //             fontWeight: '700',
// //             fontSize: '12px',
// //             fontFamily: FONT,
// //           }}
// //         >
// //           Buy tokens with MEV →
// //         </a>

// //         {/* ── Divider ── */}
// //         <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.5, letterSpacing: '1px' }}>
// //           — OR —
// //         </div>

// //         {/* ── Fallback action: Use Lc ── */}
// //         {blockData?.canUseLc ? (
// //           <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
// //             <button
// //               onClick={this.handleLcFallback}
// //               disabled={lcFallbackLoading}
// //               style={{
// //                 background: 'none',
// //                 border: `1px solid ${ACCENT}`,
// //                 borderRadius: '6px',
// //                 color: ACCENT,
// //                 fontWeight: '600',
// //                 fontSize: '12px',
// //                 fontFamily: FONT,
// //                 padding: '7px 16px',
// //                 cursor: lcFallbackLoading ? 'not-allowed' : 'pointer',
// //                 opacity: lcFallbackLoading ? 0.6 : 1,
// //               }}
// //             >
// //               {lcFallbackLoading
// //                 ? 'Processing…'
// //                 : `Use ${(blockData.lcCostWithPremium / 100).toFixed(2)} Lc instead (${blockData.lcPremiumPct}% premium)`
// //               }
// //             </button>
// //             <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.7, textAlign: 'center', maxWidth: '260px', lineHeight: '1.5' }}>
// //               You have {(blockData.livePoints / 100).toFixed(2)} Lc available.
// //               Tokens are the standard way to fund agents — Lc costs {blockData.lcPremiumPct}% more.
// //             </div>
// //           </div>
// //         ) : (
// //           <div style={{ fontSize: '11px', color: ACCENT, opacity: 0.7, textAlign: 'center', maxWidth: '260px', lineHeight: '1.5' }}>
// //             {blockData?.livePoints > 0
// //               ? `You have ${(blockData.livePoints / 100).toFixed(2)} Lc but need ${(blockData.lcCostWithPremium / 100).toFixed(2)} Lc to use this option.`
// //               : 'You have no Lc available either. Please buy tokens with MEV to continue.'
// //             }
// //           </div>
// //         )}

// //         {/* ── Lc fallback error ── */}
// //         {lcFallbackError && (
// //           <div style={{ fontSize: '11px', color: '#c62828', marginTop: '4px' }}>
// //             {lcFallbackError}
// //           </div>
// //         )}

// //         {/* ── Explanation ── */}
// //         <div style={{ fontSize: '10px', color: ACCENT, opacity: 0.6, textAlign: 'center', maxWidth: '280px', lineHeight: '1.6', marginTop: '4px' }}>
// //           Lc is your development reward currency — earned through tests and pledges.
// //           Buy tokens with MEV to fund your agents at the standard rate and preserve your Lc rewards.
// //         </div>

// //       </div>
// //     );

// //   } else {
// //     // Pledge upgrade — unchanged
// //     actionElement = (
// //       <a
// //         href="https://www.patreon.com/mevision"
// //         target="_blank"
// //         rel="noopener noreferrer"
// //         style={{ color: ACCENT, textDecoration: 'underline', fontWeight: '600' }}
// //       >
// //         Upgrade your pledge →
// //       </a>
// //     );
// //   }

// //   blockedBanner = (
// //     <div style={S.blockedBanner}>
// //       {blockMessage}
// //       <div style={{ marginTop: '10px' }}>
// //         {actionElement}
// //       </div>
// //     </div>
// //   );
// // }

//     return (
//       <div>
//         {messageBubbles}
//         {loading && this.state.showTypingIndicator ? <TypingIndicator /> : null}      
//         {blockedBanner}
//         <div ref={this.messagesEndRef} />
//       </div>
//     );
//   };

// ── Chat panel ─────────────────────────────────────────────────────────────

renderChatPanel = () => {
const {
  activeAgent, inputText, loading, mobile, showUnsubscribeConfirm, unsubscribing,
  selectedFile, uploadingFile, uploadProgress, fileError,
  isRecording, transcribing, recordingDuration, recordingMode,
  browsingAgents,
} = this.state;
  if (!activeAgent) return null;

  const charCount = inputText.length;
  const isFullWidth = !browsingAgents; // full-width when the agent grid isn't shown alongside

  // Toggle button responsive styling
  const toggleStyle = {
    width: mobile ? '34px' : '38px',
    height: '44px',
    flexShrink: 0,
    backgroundColor: 'transparent',
    border: `1px solid ${BORDER}`,
    borderRadius: '6px',
    color: this.state.enterToSend ? ACCENT : TEXT_MUTE,
    fontSize: mobile ? '16px' : '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '6px',
  };

  return (
   <div style={S.chatPanel(mobile, isFullWidth)}>    
      {/* ── Chat header ── */}
      <div style={S.chatHeader}>
        <div style={S.chatHeaderMainRow}>
          <div style={S.chatHeaderLeft}>
           {this.renderAgentIcon(
              activeAgent,
              activeAgent.avatar ? (isFullWidth ? 44 : 28) : (isFullWidth ? 32 : 16),
              { transition: 'all 0.2s' }
            )}
            <span style={S.chatAgentName(isFullWidth)}>{activeAgent.label}</span>
          </div>
          <button
            style={S.focusBtn}
            onClick={() => this.setState({ showFocusModal: true })}
            title="Open focus mode — larger view with math keyboard"
          >
            ⛶
          </button>
        </div>
        <div style={S.chatHeaderLinksRow}>
          <button
            style={S.unsubscribeLink}
            onClick={this.toggleBrowseAgents}
            title="Show all agents alongside this chat"
          >
            {browsingAgents ? 'Hide agent list' : 'Browse all agents'}
          </button>
          <button
            style={{
              ...S.unsubscribeLink,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            onClick={this.openVoiceModal}
            title="Configure this agent's voice"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
            Voice
          </button>
          {!this.state.blocked && (
            <button style={S.unsubscribeLink} onClick={this.handleUnsubscribeClick}>
              Unsubscribe
            </button>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
    <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
  <div
    style={S.messages}
    ref={this.messagesRef}
    onScroll={this.handleMessagesScroll}
  >
    {this.renderMessages()}
  </div>

  {/* Floating scroll-to-top button — only visible after the user scrolls down */}
  {this.state.showScrollTop && (
    <button
      onClick={this.scrollToTop}
      style={{
        position: 'absolute',
        top: '10px',
        right: '12px',
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.95)',
        border: `1px solid ${BORDER}`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
        color: ACCENT,
        zIndex: 2,
        padding: 0,
        transition: 'opacity 0.2s',
      }}
      title="Scroll to top"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </svg>
    </button>
  )}
</div>

      {/* ── Char count + Toggle Row ── */}
      <div style={{ padding: '0 12px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {charCount > 0 && <div style={S.charCount}>{charCount} chars</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
          <input
            type="file"
            accept="application/pdf"
            ref={this.fileInputRef}
            style={{ display: 'none' }}
            onChange={this.handleFileSelected}
          />
          {/* ── Mic button (hold on mobile, tap on desktop) ── */}
          <button
            onClick={() => this.setState(prev => ({ enterToSend: !prev.enterToSend }))}
            style={{
              fontSize: '13px',
              color: this.state.enterToSend ? ACCENT : TEXT_MUTE,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 8px',
              borderRadius: '4px',
            }}
            title={this.state.enterToSend 
              ? "Enter = Send   |   Shift+Enter = New line" 
              : "Enter = New line   |   Click Send to send"}
          >
            {this.state.enterToSend ? '⏎ Send' : '⏎ New Line'}
          </button>
          {/* ── Dictate-to-text button (Aa icon) ── */}
          <button
            style={{
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
              height: '28px',
              minWidth: '28px',
              padding: (isRecording && recordingMode === 'dictate') ? '0 8px' : 0,
              boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
              backgroundColor: (isRecording && recordingMode === 'dictate') ? '#c62828' : 'transparent',
              border: (isRecording && recordingMode === 'dictate') ? 'none' : `1px solid transparent`,
              borderRadius: '4px',
              color: (isRecording && recordingMode === 'dictate')
                ? '#fff'
                : (loading || this.state.blocked || transcribing ? TEXT_MUTE : ACCENT),
              cursor: (loading || this.state.blocked || transcribing) ? 'not-allowed' : 'pointer',
              opacity: (loading || this.state.blocked || transcribing) ? 0.5 : 1,
            }}
          onPointerDown={this.handleDictatePointerDown}
          onPointerUp={this.handleDictatePointerUp}
          onPointerCancel={this.handleDictatePointerUp}
          //onClick={(e) => alert('Dictate onClick fired')} 
            disabled={loading || this.state.blocked || transcribing || (isRecording && recordingMode === 'voice')}
            title={mobile ? 'Hold to dictate text' : (isRecording && recordingMode === 'dictate' ? 'Click to stop' : 'Dictate text into the message box')}
          >
            {(isRecording && recordingMode === 'dictate') ? (
              <>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff">
                  <circle cx="12" cy="12" r="8" />
                </svg>
                <span style={{ fontSize: '11px', fontWeight: 600 }}>
                  {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}
                </span>
              </>
            ) : (
              // "Aa" glyph styled clearly so it doesn't look like the mic
              <span style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.5px' }}>
                Aa<span style={{ fontSize: '8px', verticalAlign: 'middle', marginLeft: '1px' }}>🎤</span>
              </span>
            )}
          </button>

          <button
            style={S.toolBtn(loading || this.state.blocked || uploadingFile)}
            onClick={this.handleAttachClick}
            disabled={loading || this.state.blocked || uploadingFile}
            title="Attach a PDF"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>

        </div>
      </div>

      {/* ── Attachment preview ── */}

      {selectedFile && (
        <div style={S.attachmentChip}>
          <span style={S.attachmentChipIcon}>📎</span>
          <span style={S.attachmentChipName}>{selectedFile.name}</span>
          <button style={S.attachmentChipRemove} onClick={this.clearSelectedFile} disabled={uploadingFile}>✕</button>
        </div>
      )}
      {fileError && (
        <div style={{ color: '#c0392b', fontSize: '13px', margin: '0 12px 6px' }}>{fileError}</div>
      )}

 {/* ── Math keyboard ── */}
{this.state.showMathKeyboard && (
  <MathKeyboard
    textareaRef={this.textareaRef}
    onInsert={(newValue, cursorPos) => {
      this.setState({ inputText: newValue }, () => {
        const ta = this.textareaRef.current;
        if (ta) {
          ta.style.height = 'auto';
          ta.style.height = Math.min(ta.scrollHeight, 110) + 'px';
          if (cursorPos !== undefined) {
            ta.selectionStart = cursorPos;
            ta.selectionEnd   = cursorPos;
            ta.focus();
          }
        }
      });
    }}
    onClose={() => this.setState({ showMathKeyboard: false })}
  />
)}

{/* ── Input row — add ∑ toggle button ── */}
      <div style={S.inputRow}>
        <button
          style={S.mathToggleBtn(this.state.showMathKeyboard)}
          onClick={() => this.setState(prev => ({ showMathKeyboard: !prev.showMathKeyboard }))}
          title="Math keyboard"
        >
          ∑
        </button>
        <textarea
          ref={this.textareaRef}
          style={S.textarea}
          value={inputText}
          onChange={this.handleInputChange}
          onKeyDown={this.handleKeyDown}
          placeholder="Type your message…"
          rows={1}
          disabled={loading || this.state.blocked}
        />
      {/* ── Unified action button — mic when empty, send arrow when text present (WhatsApp model) ── */}
      {(() => {
        const hasText = !!inputText.trim();
        const hasAttachment = !!selectedFile;
        const showSendMode = hasText || hasAttachment;
        const isBusy = loading || transcribing || uploadingFile;
        const isDisabled = this.state.blocked
          || isBusy
          || (showSendMode ? false : (isRecording && recordingMode === 'dictate'));

        // Background: red while recording voice, green otherwise
        const bg = (isRecording && recordingMode === 'voice')
          ? '#c62828'
          : '#25D366';

        const onPress = (e) => {
          if (isDisabled) return;
          if (showSendMode) {
            // Send mode: behave like the old Send button
            this.sendMessage();
          } else {
            // Mic mode: toggle voice recording
            this.handleMicPointerDown(e);
          }
        };

        return (
          <button
            style={{
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
              width: '44px',
              height: '44px',
              flexShrink: 0,
              padding: 0,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: bg,
              border: 'none',
              borderRadius: '50%',
              color: '#ffffff',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              alignSelf: 'flex-end',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              transition: 'background-color 0.2s',
            }}
            onPointerDown={onPress}
            disabled={isDisabled}
            title={
              showSendMode
                ? (uploadingFile ? `Uploading… ${uploadProgress}%` : 'Send message')
                : (isRecording && recordingMode === 'voice')
                  ? 'Tap to stop and send voice note'
                  : 'Tap to record a voice note'
            }
          >
            {uploadingFile ? (
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff' }}>{uploadProgress}%</span>
            ) : transcribing ? (
              <span style={{ fontSize: '14px', color: '#fff' }}>…</span>
            ) : (isRecording && recordingMode === 'voice') ? (
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}
              </span>
            ) : showSendMode ? (
              // Send arrow — points up-right, WhatsApp style
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            ) : (
              // Mic icon
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>
        );
      })()}
      </div>

      {/* ── Unsubscribe confirmation ── */}
      {showUnsubscribeConfirm && (
        <div style={S.confirmOverlay}>
          <div style={S.confirmBox}>
            <div style={S.confirmTitle}>Unsubscribe from {activeAgent.label}?</div>
            <div style={S.confirmBody}>
              You will stop receiving check-ins and messages from this agent. Your conversation history is kept, and you can come back any time by sending a new message here.
            </div>
            <div style={S.confirmActions}>
              <button style={S.confirmCancelBtn} onClick={this.cancelUnsubscribe} disabled={unsubscribing}>
                Cancel
              </button>
              <button style={S.confirmUnsubBtn} onClick={this.confirmUnsubscribe} disabled={unsubscribing}>
                {unsubscribing ? 'Unsubscribing…' : 'Unsubscribe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

_renderFocusToolbar = () => {
  const {
    inputText, loading, isRecording, transcribing,
    recordingDuration, recordingMode, uploadingFile,
  } = this.state;
  const charCount = inputText.length;
  return (
    <div style={{ padding: '0 12px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {charCount > 0 && <div style={S.charCount}>{charCount} chars</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
        <input type="file" accept="application/pdf" ref={this.fileInputRef} style={{ display: 'none' }} onChange={this.handleFileSelected} />
        <button
          onClick={() => this.setState(prev => ({ enterToSend: !prev.enterToSend }))}
          style={{ fontSize: '13px', color: this.state.enterToSend ? ACCENT : TEXT_MUTE, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', borderRadius: '4px' }}
          title={this.state.enterToSend ? "Enter = Send   |   Shift+Enter = New line" : "Enter = New line   |   Click Send to send"}
        >
          {this.state.enterToSend ? '⏎ Send' : '⏎ New Line'}
        </button>
        <button
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0,0,0,0.1)', height: '28px', minWidth: '28px', padding: (isRecording && recordingMode === 'dictate') ? '0 8px' : 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', backgroundColor: (isRecording && recordingMode === 'dictate') ? '#c62828' : 'transparent', border: (isRecording && recordingMode === 'dictate') ? 'none' : '1px solid transparent', borderRadius: '4px', color: (isRecording && recordingMode === 'dictate') ? '#fff' : (loading || this.state.blocked || transcribing ? TEXT_MUTE : ACCENT), cursor: (loading || this.state.blocked || transcribing) ? 'not-allowed' : 'pointer', opacity: (loading || this.state.blocked || transcribing) ? 0.5 : 1 }}
          onPointerDown={this.handleDictatePointerDown}
          onPointerUp={this.handleDictatePointerUp}
          onPointerCancel={this.handleDictatePointerUp}
          disabled={loading || this.state.blocked || transcribing || (isRecording && recordingMode === 'voice')}
          title={(isRecording && recordingMode === 'dictate') ? 'Click to stop' : 'Dictate text into the message box'}
        >
          {(isRecording && recordingMode === 'dictate') ? (
            <>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff"><circle cx="12" cy="12" r="8" /></svg>
              <span style={{ fontSize: '11px', fontWeight: 600 }}>{Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}</span>
            </>
          ) : (
            <span style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.5px' }}>Aa<span style={{ fontSize: '8px', verticalAlign: 'middle', marginLeft: '1px' }}>🎤</span></span>
          )}
        </button>
        <button
          style={S.toolBtn(loading || this.state.blocked || uploadingFile)}
          onClick={this.handleAttachClick}
          disabled={loading || this.state.blocked || uploadingFile}
          title="Attach a PDF"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>
      </div>
    </div>
  );
};

_renderFocusAttachment = () => {
  const { selectedFile, uploadingFile, fileError } = this.state;
  return (
    <>
      {selectedFile && (
        <div style={S.attachmentChip}>
          <span style={S.attachmentChipIcon}>📎</span>
          <span style={S.attachmentChipName}>{selectedFile.name}</span>
          <button style={S.attachmentChipRemove} onClick={this.clearSelectedFile} disabled={uploadingFile}>✕</button>
        </div>
      )}
      {fileError && <div style={{ color: '#c0392b', fontSize: '13px', margin: '0 12px 6px' }}>{fileError}</div>}
    </>
  );
};

_renderFocusMathKeyboard = () => {
  if (!this.state.showMathKeyboard) return null;
  return (
    <MathKeyboard
      textareaRef={this.focusTextareaRef}
      onInsert={(newValue, cursorPos) => {
        this.setState({ inputText: newValue }, () => {
          const ta = this.textareaRef.current;
          if (ta) {
            ta.style.height = 'auto';
            ta.style.height = Math.min(ta.scrollHeight, 110) + 'px';
            if (cursorPos !== undefined) {
              ta.selectionStart = cursorPos;
              ta.selectionEnd   = cursorPos;
              ta.focus();
            }
          }
        });
      }}
      onClose={() => this.setState({ showMathKeyboard: false })}
    />
  );
};

_renderFocusInputRow = () => {
  const { inputText, loading, selectedFile, isRecording, transcribing, uploadingFile, uploadProgress, recordingMode } = this.state;
  const hasText = !!inputText.trim();
  const hasAttachment = !!selectedFile;
  const showSendMode = hasText || hasAttachment;
  const isBusy = loading || transcribing || uploadingFile;
  const isDisabled = this.state.blocked || isBusy || (showSendMode ? false : (isRecording && recordingMode === 'dictate'));
  const bg = (isRecording && recordingMode === 'voice') ? '#c62828' : '#25D366';
  const onPress = (e) => {
    if (isDisabled) return;
    if (showSendMode) { this.sendMessage(); }
    else { this.handleMicPointerDown(e); }
  };
  return (
    <div style={S.inputRow}>
      <button
        style={S.mathToggleBtn(this.state.showMathKeyboard)}
        onClick={() => this.setState(prev => ({ showMathKeyboard: !prev.showMathKeyboard }))}
        title="Math keyboard"
      >∑</button>
    <textarea
      ref={this.focusTextareaRef}  // ← separate ref, not this.textareaRef
      style={S.textarea}
      value={inputText}
      onChange={this.handleInputChange}
      onKeyDown={this.handleKeyDown}
      placeholder="Type your message…"
      rows={1}
      disabled={loading || this.state.blocked}
    />
      <button
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0,0,0,0.1)', width: '44px', height: '44px', flexShrink: 0, padding: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bg, border: 'none', borderRadius: '50%', color: '#ffffff', cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1, alignSelf: 'flex-end', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'background-color 0.2s' }}
        onPointerDown={onPress}
        disabled={isDisabled}
        title={showSendMode ? (uploadingFile ? `Uploading… ${uploadProgress}%` : 'Send message') : (isRecording && recordingMode === 'voice') ? 'Tap to stop and send voice note' : 'Tap to record a voice note'}
      >
        {uploadingFile ? (
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff' }}>{uploadProgress}%</span>
        ) : transcribing ? (
          <span style={{ fontSize: '14px', color: '#fff' }}>…</span>
        ) : showSendMode ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        ) : (isRecording && recordingMode === 'voice') ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        )}
      </button>
    </div>
  );
};

  // ── Render ─────────────────────────────────────────────────────────────────
  render() {
    const { activeCategory, viewMode, activeAgent, mobile, connectedAgents, showConnectedAgents, browsingAgents } = this.state;    const { user } = this.props;

    const pledge = user?.pledge || 0;
    const pledgeDisplay = pledge > 0 ? `$${(pledge / 100).toFixed(0)}/mo` : 'No pledge';

    const agents         = this.filteredAgents();
    const chatOpen       = !!activeAgent;
    // Dennis (or whichever agent is active) is the default full-focus view.
    // The grid only appears when there's no active chat, or the user explicitly
    // asked to browse via the "Browse all agents" link in the chat header.
    const showChatPanel  = chatOpen && !(mobile && browsingAgents);
    const showAgentPanel = !chatOpen || browsingAgents;
    const heroMode       = chatOpen && !browsingAgents; // the featured agent is fully in focus — quiet down the surrounding chrome

    // ← add this line:
    const tokenBalance = ((parseFloat(user?.tokens || 0)) / 100).toFixed(2);


    return (
      <div style={S.page}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.headerTopRow}>
            <div style={S.logoMark}>ThinkLead · HAI</div>
            <div style={S.statsInline} ref={this.statsRef}>
              {chatOpen && (
                <>
                  <button style={S.backBtnSubtle} onClick={this.closeChat}>← Agents</button>
                  <span style={S.statsDot}>·</span>
                </>
              )}
              <span>Pledge <span style={S.statsValue}>{pledgeDisplay}</span></span>
              <span style={S.statsDot}>·</span>
              <span>Tokens <span style={S.statsValue}>{tokenBalance}</span></span>
              <span style={S.statsDot}>·</span>
              <span
                style={S.connectedAgentsRowInline}
                onClick={() => this.setState(prev => ({ showConnectedAgents: !prev.showConnectedAgents }))}
              >
                {connectedAgents.length} agent{connectedAgents.length !== 1 ? 's' : ''} connected
              </span>
              {showConnectedAgents && connectedAgents.length > 0 && (
                <div style={S.connectedAgentsList}>
                  {connectedAgents.map(a => (
                    <div key={a.agentId} style={S.connectedAgentsItem}>{a.label}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <h1 style={S.headerTitle(heroMode)}>Agent Dashboard</h1>
          {!heroMode && (
            <div style={S.headerSubtitle}>
              {ALL_AGENTS.length} development partners · {user?.firstName || 'Leader'}
            </div>
          )}
          {/* ── Notification permission status ── */}
          {/* <div style={{ marginTop: '12px' }}>
            <NotificationStatus uid={user?.uid || user?.id} />
          </div> */}
          {/* ── Toolbar: category tabs + view toggle — only meaningful when the grid is visible ── */}
          {showAgentPanel && (
            <div style={S.toolbar}>
              <div style={S.tabs}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    style={S.tab(activeCategory === cat)}
                    onClick={() => this.setState({ activeCategory: cat })}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div style={S.viewToggle}>
                <button
                  style={{ ...S.viewBtn(viewMode === 'compact'), borderRight: `1px solid ${BORDER}` }}
                  onClick={() => this.setState({ viewMode: 'compact' })}
                  title="Icon view"
                >⊞</button>
                <button
                  style={{ ...S.viewBtn(viewMode === 'expanded'), borderRight: 'none' }}
                  onClick={() => this.setState({ viewMode: 'expanded' })}
                  title="Expanded view"
                >☰</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Main content ── */}
        <div style={S.main}>

          {/* Agent grid — the default landing view when there's no chat, or a click away via "Browse all agents" */}
          {showAgentPanel && (
            <div style={S.agentPanel(showChatPanel, mobile)}>
              {chatOpen && browsingAgents && (
                <button
                  style={{ ...S.backBtn, marginBottom: '12px' }}
                  onClick={() => this.setState({ browsingAgents: false })}
                >
                  ← Back to {activeAgent.label}
                </button>
              )}
              <div style={viewMode === 'compact' ? S.gridCompact : S.gridExpanded}>
                {agents.map(agent =>
                  viewMode === 'compact'
                    ? this.renderCardCompact(agent)
                    : this.renderCardExpanded(agent)
                )}
              </div>
            </div>
          )}

     {/* Chat panel */}
        {showChatPanel && this.renderChatPanel()}
      </div>

      {/* ── Voice configuration modal (top-level so it escapes chat-panel overflow) ── */}
      {this.state.showVoiceModal && activeAgent && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
          onClick={this.closeVoiceModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '10px',
              padding: '20px',
              maxWidth: '380px',
              width: '100%',
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              boxSizing: 'border-box',
            }}
        onClick={(e) => e.stopPropagation()}
          >
            <div style={S.confirmTitle}>Choose {activeAgent.label}'s voice</div>
            <div style={{ ...S.confirmBody, marginBottom: '12px' }}>
              Pick how this agent sounds when replying to your voice notes. Tap the play button to preview each voice.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' }}>
              {VOICE_OPTIONS.map(opt => {
                const isSelected = this.state.selectedVoice === opt.id;
                const isPlaying = this.state.previewingVoice === opt.id;
                const isDefault = opt.id === this.state.defaultVoice;
                return (
                  <div
                    key={opt.id}
                    onClick={() => this.setState({ selectedVoice: opt.id })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: `1.5px solid ${isSelected ? ACCENT : BORDER}`,
                      backgroundColor: isSelected ? ACCENT_F : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); this.previewVoice(opt.id); }}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: ACCENT,
                        color: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: '12px',
                      }}
                      title={isPlaying ? 'Stop' : 'Preview'}
                    >
                      {isPlaying ? '⏸' : '▶'}
                    </button>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT_H, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {opt.label}
                        {isDefault && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            color: ACCENT,
                            backgroundColor: ACCENT_F,
                            padding: '2px 6px',
                            borderRadius: '3px',
                            textTransform: 'uppercase',
                          }}>Default</span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: TEXT_MID }}>{opt.description}</div>
                    </div>

                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? ACCENT : BORDER}`,
                      backgroundColor: isSelected ? ACCENT : 'transparent',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={S.confirmActions}>
              <button
                style={S.confirmCancelBtn}
                onClick={this.closeVoiceModal}
                disabled={this.state.savingVoice}
              >
                Cancel
              </button>
              <button
                style={{
                  ...S.confirmUnsubBtn,
                  backgroundColor: ACCENT,
                }}
                onClick={this.saveVoiceSelection}
                disabled={this.state.savingVoice}
              >
                {this.state.savingVoice ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    {this.state.showFocusModal && this.state.activeAgent && (
      <AgentFocusModal
        agent={this.state.activeAgent}
        welcome={WELCOME_BY_AGENT[this.state.activeAgent.id] || null}
        messages={this.state.messages}
        loading={this.state.loading}
        blocked={this.state.blocked}
        blockReason={this.state.blockReason}
        blockMessage={this.state.blockMessage}
        renderBlockedBanner={this.renderBlockedBanner}
        onClose={() => this.setState({ showFocusModal: false })}
        renderToolbar={() => this._renderFocusToolbar()}
        renderInputRow={() => this._renderFocusInputRow()}
        renderAttachment={() => this._renderFocusAttachment()}
        renderMathKeyboard={() => this._renderFocusMathKeyboard()}
        uid={this.props.user?.uid}
        agentId={this.state.activeAgent?.id}
        showTypingIndicator={this.state.showTypingIndicator}   // 
        onMessageAutoplayed={this.markMessageAutoplayed}   // 
      />
    )}

    </div>
  );
}
}

const mapStateToProps = (state) => ({
  user: state.userDialog.userDetails[0] || null,
});

export default connect(mapStateToProps)(AgentDashboard);